import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { createClient, ApiError } from "@kundeninfo/api-client";
import type { KundeListe as KundeListeEintrag, KundeAkte as KundeAkteDaten,
              Unternehmen } from "@kundeninfo/api-client";
import { MeSchale, useEinspalter, applyTheme, setzeAktionsfarben, Icon, useFocusTrap,
         UMGEBUNGSFARBEN, schriftAus, geraetErmitteln } from "@kundeninfo/ui";
import type { PanelState } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { KundenListe } from "./views/KundenListe.js";
import type { KundenFehler } from "./views/KundenListe.js";
import { KundenAkte } from "./views/KundenAkte.js";
import type { AktenTabId } from "./views/KundenAkte.js";
import { KundeAnlegen } from "./views/KundeAnlegen.js";
import { Kopfzeile, Wortmarke } from "./views/Kopfzeile.js";
import { Tableiste, bereicheFuer, zielRoute } from "./views/Navigation.js";
import type { NavigationsZiel } from "./views/Navigation.js";
import { Kontext } from "./views/Kontext.js";
import { useSchnittstellen } from "./views/Schnittstellen.js";
import { useBereichAngebote } from "./views/BereichAngebote.js";
import { useBereichAuftraege } from "./views/BereichAuftraege.js";
import type { AuftragAusAngebot } from "./views/BereichAuftraege.js";
import { useBereichArtikel } from "./views/BereichArtikel.js";
import { useBereichRechnungen } from "./views/BereichRechnungen.js";
import type { RechnungAusAuftrag } from "./views/BereichRechnungen.js";
import { RECHNUNGS_FILTER, alleFilter } from "./bereichsfilter.js";
import type { AngebotsFilter, AuftragsFilter, RechnungsFilter } from "./bereichsfilter.js";
import { Einstellungen } from "./views/Einstellungen.js";
import type { Preferences, EinstellungsGruppe } from "./views/Einstellungen.js";
import { EinstellungenSpalte } from "./views/EinstellungenSpalte.js";
import { Verwaltung, objektlisteVon } from "./views/Verwaltung.js";
import type { VerwaltungsBereich } from "./views/Verwaltung.js";
import { VerwaltungsSpalte } from "./views/VerwaltungsSpalte.js";
import { useVerwaltungUnternehmen } from "./views/verwaltung/unternehmen.js";
import { useVerwaltungBenutzer } from "./views/verwaltung/benutzer.js";
import { useVerwaltungKonditionen } from "./views/verwaltung/zahlungskonditionen.js";
import { useVerwaltungArtikel } from "./views/verwaltung/artikel.js";
import { useVerwaltungTextbausteine } from "./views/verwaltung/textbausteine.js";
import { useVerwaltungAngebotsvorlagen } from "./views/verwaltung/angebotsvorlagen.js";
import { useVerwaltungLayout } from "./views/verwaltung/layout.js";
import { ObjektBereich } from "./views/verwaltung/gemeinsam.js";
import type { VerwaltungBereichTeile } from "./views/verwaltung/gemeinsam.js";
import { arbeitsunternehmen, darfSperreUebernehmen } from "./session.js";
import type { Sitzung } from "./session.js";
import { umgebungAusSitzung, titelPraefix } from "./umgebung.js";
import { anwendungsname, wirksamerZuschnitt, zuschnittAusSitzung, BELEGDESIGN_VERWALTUNG }
  from "./zuschnitt.js";
import type { Zuschnitt } from "./zuschnitt.js";
import type { UmgebungsAnzeige } from "@kundeninfo/ui";
import { STANDARD_PANELS, lesePanels, merkePanels, vergissPanels } from "./panelSpeicher.js";
import { routeToPath, useRoute } from "./router.js";
import type { Route, Herkunft } from "./router.js";
import "./App.css";

const client = createClient();

/**
 * Die leere Unternehmensliste — EINE Instanz für alle vier Bereichs-Hooks.
 *
 * `sitzung?.companies ?? []` schrieb bei jedem Zeichnen ein FRISCHES Array in `companies`.
 * Solange die Sitzung noch nicht steht (und an jedem Zeichnen davor), war das jedes Mal
 * eine andere Referenz — und die Bereiche führen `companies` in Abhängigkeitslisten. Die
 * Wirkung ist heute harmlos, aber sie ist auch nicht zugesichert: Jede Abhängigkeitsliste,
 * die diesen Wert künftig aufnimmt, liefe grundlos mit. Eine Konstante auf Modulebene
 * kostet nichts und nimmt der Frage die Spitze.
 *
 * `Unternehmen[]` und nicht `readonly Unternehmen[]`: Die Bereiche erwarten die
 * veränderbare Gestalt aus dem Klienten; geschrieben wird hier nichts (der Wert steht in
 * keiner Zeile, die etwas hineinlegt).
 */
const LEERE_UNTERNEHMEN: Unternehmen[] = [];

/**
 * Was in der Listenspalte des Rahmens steht — die Liste, aus der man auf diesem
 * Bildschirm weiterblättert.
 *
 * Seit dem 23.09.2026 trägt JEDE Adresse der Hauptnavigation eine Spalte. Vier davon sind
 * NAVIGATION (Kundenliste, Bereichs-Untermenü der Verwaltung, Dienstliste, Gruppen-Untermenü
 * der Einstellungen), vier sind OBJEKTLISTEN (Angebote, Aufträge, Artikel, Rechnungen).
 * `null` bleibt allein der unbekannten Adresse.
 *
 * Die vier Objektbereiche hatten sie schon bis zum 07.09.2026; dann entfiel sie („Filterkopf
 * statt zweiter Spalte": alles untereinander im Inhalt), und seit dem 23.09.2026 haben sie
 * sie zurück — Entscheid Dirk am Live-System: „die Liste dann als Spalte anzeigen und die
 * Übersicht als Inhaltsbereich". Der FILTERKOPF, der am 07.09. entstand, bleibt und steht
 * jetzt oben IN dieser Spalte (`lage="spalte"`).
 */
export type SpaltenInhalt =
  "kunden" | "angebote" | "auftraege" | "artikel" | "rechnungen" | "verwaltung"
  | "schnittstellen" | "einstellungen";

/**
 * Welche Liste bekommt dieser Bildschirm an die Seite? `null` heisst „gar keine" — dann
 * zeichnet der Rahmen die Spalte nicht und die Ansicht steht in voller Breite da.
 *
 * Befund Dirk vom 05.09.2026: „Kunden sollten nur im Punkt Kunden angezeigt werden." Bis
 * dahin reichte App.tsx die Kundenliste FEST in die Listenspalte des Rahmens — ein
 * Überbleibsel aus der Zeit, als „Kunden" das einzige Modul war. Auf Angeboten, Aufträgen,
 * Artikeln, Schnittstellen, in den Einstellungen und in der Verwaltung stand sie seither
 * sinnlos daneben und nahm der eigentlichen Ansicht ein Drittel der Breite weg.
 *
 * Genau DREI Adressen zählen zu „Kunden": die Liste selbst, eine geöffnete Akte und die
 * Anlege-Maske. In allen dreien ist die Kundenliste der Ort, an dem man weiterblättert.
 *
 * Diese Funktion ist die EINE Stelle, an der über den Spalteninhalt entschieden wird —
 * vollständig aufgezählt statt mit einem `sonst`-Zweig, aus demselben Grund wie bei
 * zielRoute() oben: Kommt eine Adresse hinzu, meldet das der Typprüfer, statt sie stumm
 * der einen oder anderen Liste zuzuschlagen. Die unbekannte Adresse bekommt bewusst
 * KEINE: Dort steht nur der Hinweis, dass es die Seite nicht gibt.
 *
 * Entschieden wird allein über den Adressnamen: Ob ein Objekt geöffnet ist (`id`) oder
 * woher man kam (`herkunft`), ändert die Spalte nicht — sonst verschwände sie beim
 * Anklicken eines Eintrags. Festgehalten in test/spalteFuer.test.ts.
 */
export function spalteFuer(route: Route): SpaltenInhalt | null {
  switch (route.name) {
    case "kundenliste": case "kunde": case "kundeNeu": return "kunden";
    /*
     * Die vier OBJEKTBEREICHE haben ihre Spalte seit dem 23.09.2026 zurück: Liste links,
     * gewähltes Objekt als Inhalt rechts. Liste und geöffnetes Objekt sind DERSELBE
     * Bildschirm in zwei Zuständen und bekommen deshalb dieselbe Spalte — sonst verschwände
     * sie beim Anklicken einer Zeile. Der Statusfilter in der Adresse ändert daran ebenfalls
     * nichts: Er wählt aus, WAS in der Spalte steht, nicht OB.
     */
    case "angebote": return "angebote";
    case "auftraege": return "auftraege";
    case "artikel": return "artikel";
    case "rechnungen": return "rechnungen";
    // Die Verwaltung (Etappe 2, Aufgabe 1): dasselbe Muster, nur mit ihrem Bereichs-
    // Untermenü in der Spalte. Übersicht und offener Bereich bekommen DIESELBE Spalte,
    // sonst verschwände das Untermenü beim Anklicken eines Eintrags.
    case "verwaltung": return "verwaltung";
    // Etappe 3: dieselbe Regel noch einmal, für die letzten zwei Bereiche. Links die
    // Dienste bzw. die Einstellungsgruppen, rechts der gewählte — und wie überall
    // bekommen „nichts gewählt" und „etwas gewählt" DIESELBE Spalte, sonst verschwände
    // sie beim Anklicken eines Eintrags.
    case "schnittstellen": return "schnittstellen";
    case "einstellungen": return "einstellungen";
    // Die unbekannte Adresse bekommt bewusst keine: Dort steht nur der Hinweis, dass es
    // die Seite nicht gibt.
    case "unbekannt": return null;
  }
}

/**
 * Steht rechts die KUNDEN-Kontextspalte? `false` heisst „gar keine" — dann zeichnet der
 * Rahmen sie nicht und die Ansicht bekommt ihre Breite dazu (siehe `MeSchaleProps.context`).
 *
 * Review-Befund vom 06.09.2026: Bis dahin reichte App.tsx den Kundenkontext FEST an den
 * Rahmen — dasselbe Überbleibsel wie bei der Listenspalte, nur eine Spalte weiter rechts.
 * In der Verwaltung, in den Einstellungen und bei den Schnittstellen stand dort unverändert
 * „Kein Kunde ausgewählt." und nahm der eigentlichen Ansicht rund 280 Pixel weg. Sichtbar
 * wurde das an den Verwaltungs-Objektbereichen: Liste und Objekt standen erst auf sehr
 * breiten Fenstern nebeneinander, weil dem Inhalt links wie rechts eine Spalte fehlte.
 *
 * Es sind DIESELBEN drei Adressen wie bei der Kundenliste (siehe `spalteFuer`) — beide
 * Aussagen betreffen den Kunden, und ein halb betretener Kundenbereich wäre keiner. Die
 * Entscheidung steht hier trotzdem eigenständig und wird nicht aus `spalteFuer`
 * abgeleitet: Das eine sagt, WORAUS man weiterblättert, das andere, ZU WEM der Bildschirm
 * gehört. Dass beide heute deckungsgleich sind, hält zeigtKundenKontext.test.ts Adresse für
 * Adresse fest — eine künftige Abweichung wäre damit eine bewusste und keine stumme.
 *
 * Vollständig aufgezählt statt mit einem `sonst`-Zweig, aus demselben Grund wie bei
 * `zielRoute` und `spalteFuer`: Eine neue Adresse soll der Typprüfer melden, statt ihr die
 * Kontextspalte stumm zu geben oder zu nehmen.
 *
 * Entschieden wird allein über den Adressnamen: Ob ein Kunde geöffnet ist, ändert die
 * SPALTE nicht — nur ihren Inhalt (ohne Auswahl steht dort der Hinweis). Sonst erschiene
 * sie beim Anklicken eines Eintrags und verschwände beim Zurückgehen.
 */
export function zeigtKundenKontext(route: Route): boolean {
  switch (route.name) {
    // Die drei Kunden-Adressen: die Liste, eine geöffnete Akte und die Anlege-Maske.
    case "kundenliste": case "kunde": case "kundeNeu": return true;
    // Alle übrigen Bereiche haben ihren eigenen Gegenstand — ein Kundenkontext stünde dort
    // sinnlos daneben. Die Rechnungen eingeschlossen: Ihre Liste ist seit dem Paket
    // „Rechnungsstatus" nach Status gegliedert, nicht nach Kunden.
    case "angebote": case "auftraege": case "artikel": case "rechnungen":
    case "verwaltung": case "schnittstellen": case "einstellungen":
    // Und die unbekannte Adresse gehört zu niemandem: Dort steht nur der Hinweis, dass es
    // die Seite nicht gibt.
    case "unbekannt": return false;
  }
}

/**
 * Steht auf dem iPhone-Einspalter die SPALTE im Vordergrund (statt des Details)?
 *
 * Bis Etappe 1, Aufgabe 3 stand hier schlicht `route.name === "kundenliste"` — es gab nur
 * eine Spalte, und die gehörte den Kunden. Mit den Bereichs-Spalten gilt die Regel
 * allgemein: Solange kein Objekt gewählt ist, zeigt der Einspalter die Liste; ein
 * gewähltes Objekt füllt den Bildschirm, und der Rückweg im Kopf führt zur Liste zurück
 * (siehe `zurueckWeg`).
 *
 * Vollständig aufgezählt statt mit einem `sonst`-Zweig, aus demselben Grund wie bei
 * `spalteFuer`: Eine neue Adresse soll der Typprüfer melden.
 */
function spalteZuerst(route: Route): boolean {
  switch (route.name) {
    case "kundenliste": return true;
    /*
     * Die vier Objektbereiche: Solange kein Objekt gewählt ist, zeigt der Einspalter die
     * Liste; ein gewähltes Objekt füllt den Bildschirm, und der Rückweg im Kopf führt auf die
     * Listenadresse samt ihrem Filter zurück (siehe `zurueckWeg` — die Regel steht dort schon
     * so, sie war zwischen dem 07. und dem 23.09.2026 nur ohne Wirkung).
     *
     * Die Angabe galt schon für alle vier, bevor sie alle eine Spalte hatten (Artikel ab
     * Aufgabe 4, Angebote ab Aufgabe 5, Aufträge ab Aufgabe 6, Rechnungen ab Aufgabe 7) —
     * das war gefahrlos und keine Schlamperei: Der Rahmen zeigt ohne `list` immer das
     * Detail, ganz egal was `showList` sagt (siehe MeSchale.tsx, `p.showList && hatListe`).
     * Für einen Bereich ohne Spalte war die Angabe also wirkungslos und wurde erst mit
     * seiner Spalte wirksam. Seit Aufgabe 7 haben alle vier ihre Spalte, und die Angabe
     * gilt für alle vier mit Wirkung.
     */
    case "angebote": case "auftraege": case "artikel": case "rechnungen":
      return route.id === undefined;
    /*
     * Die Verwaltung (Etappe 2, Aufgabe 1) folgt derselben Regel — ihr „gewähltes Objekt"
     * ist der BEREICH: Ohne Bereich zeigt der Einspalter das Untermenü, ein Antippen öffnet
     * den Bereich in voller Breite, und der Rückweg im Kopf führt auf das Untermenü zurück
     * (siehe `zurueckWeg`). Genau dafür ist `bereich` optional (siehe router.ts).
     *
     * Zwischen Aufgabe 2 und dem Entscheid vom 06.09.2026 hatte sie hier eine Stufe mehr:
     * Ein Bereich MIT Objektliste klappte sie in der SPALTE auf, also blieb der Einspalter
     * dort bei der Spalte, bis ein Objekt gewählt war. Seit die Objektlisten wieder im
     * INHALT stehen, gilt die einfache Regel wieder für alle zehn Bereiche — und die Stufe
     * darunter (Liste ODER Objekt) faltet der Inhalt selbst (siehe `ObjektBereich`).
     */
    case "verwaltung": return route.bereich === undefined;
    /*
     * Die Schnittstellen und die Einstellungen (Etappe 3) folgen derselben Regel wie die
     * Objektbereiche: Ohne gewählten Dienst bzw. ohne gewählte Gruppe zeigt der Einspalter
     * die Spalte, ein Antippen öffnet den Inhalt in voller Breite, und der Rückweg im Kopf
     * führt auf die Spalte zurück (siehe `zurueckWeg`). Genau dafür sind `dienst` und
     * `gruppe` optional (siehe router.ts).
     */
    case "schnittstellen": return route.dienst === undefined;
    case "einstellungen": return route.gruppe === undefined;
    // Ein Kunde, eine Anlege-Maske, eine unbekannte Adresse: Das ist jeweils das ZIEL, das
    // der Einspalter zeigt.
    case "kunde": case "kundeNeu":
    case "unbekannt": return false;
  }
}

/**
 * Wohin der Rückweg im Kopf des iPhone-Einspalters führt — `null` heisst „von hier führt
 * keiner", und dann zeichnet der Rahmen auch keinen Knopf (siehe `MeSchaleProps.zurueck`).
 *
 * Befund vom 05.09.2026: Auf schmalen Viewports trug der Rahmen an JEDER Adresse ausser der
 * Kundenliste einen Knopf „Zurück zur Liste", der pauschal zur KUNDENliste sprang. Das war
 * an zwei Stellen falsch:
 *
 *  - Auf den globalen Listen selbst (Angebote, Aufträge, Artikel, Schnittstellen,
 *    Einstellungen, Verwaltung). Sie sind EINSTIEGSPUNKTE der Hauptnavigation — man erreicht
 *    sie über die Tab-Leiste, nicht über einen Weg, den man zurückgehen könnte. Der Knopf
 *    versprach dort einen Ursprung, den es nicht gab, und schickte einen auf einen
 *    Bildschirm, auf dem man nie war.
 *  - Nach dem Sprung AUS einer globalen Liste in ein fremdes Ziel (Angebotssuche → Akte,
 *    Katalog → Artikelpflege). Dort gibt es einen Ursprung — nur eben nicht die Kundenliste.
 *
 * Deshalb entscheidet die HERKUNFT (router.ts), nicht die Zieladresse allein. Vollständig
 * aufgezählt statt mit einem `sonst`-Zweig, aus demselben Grund wie bei `zielRoute` und
 * `spalteFuer`: Eine neue Adresse soll der Typprüfer melden, statt sie stumm dem einen
 * oder anderen Rückweg zuzuschlagen.
 */
function zurueckWeg(route: Route): Rueckziel | null {
  switch (route.name) {
    case "kundenliste":
      return null;
    /*
     * Die Schnittstellen und die Einstellungen (Etappe 3): Ihre LISTE bzw. ihr Untermenü
     * bleibt ein Einstiegspunkt der Hauptnavigation — von dort führt kein Weg zurück. Ein
     * geöffneter Dienst bzw. eine geöffnete Gruppe dagegen hat immer einen Ursprung: die
     * Spalte, die sie auf dem iPhone unter sich hat.
     *
     * Eine fremde HERKUNFT gibt es hier nicht (anders als bei den Objektbereichen und der
     * Verwaltung): In diese beiden Bereiche springt niemand aus einer fremden Liste — sie
     * sind über die Hauptnavigation erreichbar und sonst gar nicht. Deshalb trägt ihre
     * Adresse auch keine (siehe router.ts).
     */
    case "schnittstellen":
      return route.dienst === undefined ? null : "schnittstellen";
    case "einstellungen":
      return route.gruppe === undefined ? null : "einstellungen";
    /*
     * Die Bereiche Angebote, Aufträge und Artikel (Etappe 1, Aufgaben 3–5 vom 05.09.2026 —
     * die Nummern gehören dem ALTEN Etappenplan und sind nicht die Aufgaben 4–7 des Plans
     * vom 23.09.2026) haben seit ihrer eigenen Objektliste zwei Zustände. Die LISTE selbst
     * bleibt ein Einstiegspunkt der Hauptnavigation — von dort führt kein Weg zurück, ausser
     * man kam über eine fremde Herkunft. Ein GEÖFFNETES Objekt dagegen hat immer einen
     * Ursprung: die Liste, die es
     * auf dem iPhone unter sich hat — genau wie die Kundenakte ihre Kundenliste. Damit hat
     * `herkunft` an diesen Adressen seinen Verwerter.
     */
    case "angebote":
      if (route.id === undefined) return route.herkunft ?? null;
      if (route.herkunft !== undefined) return route.herkunft;
      // Seit dem 07.09.2026 trägt die Adresse auch den STATUSFILTER: Der Rückweg führt auf
      // die Liste, aus der man das Objekt geöffnet hat, und das ist eine gefilterte. Ohne
      // ihn landete man auf dem iPhone nach dem Zurück in „alle Angebote" — an einem Ort,
      // an dem man nicht war. Dieselbe Regel wie beim Rechnungsfilter darunter.
      return route.filter === undefined
        ? "angebote"
        : { ziel: { name: "angebote", filter: route.filter },
            beschriftung: ZURUECK_BESCHRIFTUNG.angebote };
    case "auftraege":
      if (route.id === undefined) return route.herkunft ?? null;
      if (route.herkunft !== undefined) return route.herkunft;
      return route.filter === undefined
        ? "auftraege"
        : { ziel: { name: "auftraege", filter: route.filter },
            beschriftung: ZURUECK_BESCHRIFTUNG.auftraege };
    case "artikel":
      // Der Artikel bleibt ohne Filtersegment (siehe router.ts) — hier genügt der Name.
      return route.id === undefined ? (route.herkunft ?? null) : (route.herkunft ?? "artikel");
    /*
     * Die Rechnungen: wie die drei Bereiche darüber — nur führt der Rückweg aus einem
     * geöffneten Beleg auf die LISTE, aus der man ihn geöffnet hat, und das ist seit dem
     * Paket „Rechnungsstatus" eine bestimmte Statusliste. Ohne den Filter landete man auf
     * dem iPhone nach dem Zurück in „Alle Rechnungen" — an einem Ort, an dem man nicht
     * war, und die eben abgearbeitete Statusliste wäre verloren. Eine fremde Herkunft
     * geht wie überall vor.
     */
    case "rechnungen":
      if (route.id === undefined) return route.herkunft ?? null;
      if (route.herkunft !== undefined) return route.herkunft;
      return route.filter === undefined
        ? "rechnungen"
        : { ziel: { name: "rechnungen", filter: route.filter },
            beschriftung: ZURUECK_BESCHRIFTUNG.rechnungen };
    /*
     * Die Verwaltung: Ihre ÜBERSICHT ist ein Einstiegspunkt der Hauptnavigation — von dort
     * führt kein Weg zurück, ausser man kam über eine fremde Herkunft („Artikel pflegen"
     * aus dem Katalog). Ein GEÖFFNETER BEREICH dagegen hat immer einen Ursprung: das
     * Untermenü, das er auf dem iPhone unter sich hat — genau wie ein geöffnetes Angebot
     * seine Angebotsliste. Die fremde Herkunft geht vor: Wer aus dem Katalog in die
     * Artikelpflege sprang, will dorthin zurück und nicht in ein Menü, in dem er nie war.
     */
    case "verwaltung": {
      if (route.bereich === undefined) return route.herkunft ?? null;
      /*
       * Seit Aufgabe 2 gibt es eine Stufe mehr: Aus einem geöffneten OBJEKT führt der
       * Rückweg auf die Objektliste seines Bereichs — nicht auf das Untermenü, in dem der
       * Bereich nur einer von zehn Einträgen wäre. Die Beschriftung NENNT das Ziel und
       * kommt deshalb aus der Bereichstabelle (deutsche Artikel lassen sich aus dem Label
       * nicht errechnen).
       *
       * Und dieser Schritt geht der HERKUNFT VOR (Aufgabe 3): Seit der Artikelstamm eine
       * Objektebene hat, kann man über „Pflege in der Verwaltung" aus dem Katalog kommen
       * UND dort einen Artikel öffnen. Schlüge die Herkunft auch dann durch, spränge der
       * Rückweg vom geöffneten Artikel in einem Satz zurück in den Katalog und überspränge
       * die Liste, aus der man ihn gerade geöffnet hat — auf dem iPhone wäre der Weg
       * zurück in den Artikelstamm damit gar nicht gehbar. Ein Rückweg geht EINE Stufe, und
       * die Stufe über einem Verwaltungs-Objekt ist die Objektliste seines Bereichs. Die
       * Herkunft ist deshalb nicht verloren: Sie steht am Bereich ohne Objekt, also genau
       * eine Stufe weiter oben, und trägt von dort in den Katalog zurück.
       */
      const liste = objektlisteVon(route.bereich);
      if (route.id !== undefined && liste !== null) {
        return {
          ziel: {
            name: "verwaltung", bereich: route.bereich,
            // Die Herkunft REIST MIT auf die Objektliste — sonst wäre der Satz oben eine
            // Behauptung ohne Deckung: Der Rückweg würde sie fallen lassen, und von der
            // Objektliste führte kein Weg mehr in den Katalog zurück.
            ...(route.herkunft === undefined ? {} : { herkunft: route.herkunft }),
          },
          beschriftung: liste.rueckweg,
        };
      }
      return route.herkunft ?? "verwaltung";
    }
    // Die Akte hat immer einen Ursprung: die globale Liste, aus der man sprang — und sonst
    // die Kundenliste, die sie auf dem iPhone unter sich hat.
    case "kunde": return route.herkunft ?? "kundenliste";
    case "kundeNeu": return "kundenliste";
    // Die unbekannte Adresse kennt ihren Ursprung nicht. Ihr eigener Knopf im Inhalt führt
    // zur Kundenliste; der Rückweg im Kopf sagt dasselbe, statt zu verschwinden.
    case "unbekannt": return "kundenliste";
  }
}

/**
 * Wohin ein Rückweg führen kann. Das sind die vier Herkünfte (die globalen Listen, aus
 * denen man in ein fremdes Ziel springt) plus die zwei Bildschirme, die eine Ansicht ohne
 * Herkunft unter sich hat: die Kundenliste und — seit Etappe 2, Aufgabe 1 — die Übersicht
 * der Verwaltungsbereiche.
 *
 * Der letzte Fall kam mit Aufgabe 2 dazu und ist der einzige, der nicht mit dem blossen
 * Namen auskommt: die OBJEKTLISTE eines Verwaltungsbereichs. Sie ist keine eigene Adresse
 * neben den anderen, sondern `/verwaltung/<bereich>` — also reist der Bereich mit, und mit
 * ihm seine Beschriftung aus VERWALTUNGS_BEREICHE.
 */
type Rueckziel =
  /** `schnittstellen` und `einstellungen` kamen mit Etappe 3 dazu — zwei weitere
   *  Bildschirme, die eine Ansicht ohne Herkunft unter sich hat: die Dienstliste und das
   *  Untermenü der Einstellungsgruppen. */
  | "kundenliste" | "verwaltung" | "schnittstellen" | "einstellungen" | Herkunft
  /**
   * Ein Rückweg auf einen Bildschirm, den kein Name allein trifft — die Objektliste eines
   * Verwaltungsbereichs (`/verwaltung/<bereich>`) und die Statuslisten der Objektbereiche
   * (`/angebote/<filter>`, `/auftraege/<filter>`, `/rechnungen/<filter>`). Sie sind keine
   * eigenen Adressnamen, sondern Zustände eines Namens; also reist die ganze ADRESSE mit,
   * samt allem, was an ihr hängt (der Filter, und in der Verwaltung die Herkunft, mit der
   * man hereinkam — sonst führte von der Objektliste kein Weg mehr in den Katalog zurück).
   *
   * Seit dem 07.09.2026 EINE Gestalt für beides statt zweier ähnlicher: Bis dahin gab es
   * je einen Fall für den Verwaltungsbereich und für den Rechnungsfilter, und mit den
   * Filtern von Angebot und Auftrag wären es vier geworden — vier Wege, dieselbe Sache zu
   * sagen („geh auf DIESE Adresse und nenne sie so").
   *
   * Die Beschriftung kommt mit, weil sie das ZIEL nennt und sich aus der Adresse nicht
   * errechnen lässt (deutsche Artikel; und ein Satz je Statusgruppe wäre eine zweite
   * Beschriftungstabelle für dieselbe Sache — „Zurück zu den Rechnungen" gilt für jede).
   */
  | { ziel: Route; beschriftung: string };

/**
 * Die Beschriftung des Rückwegs — sie NENNT das Ziel. „Zurück zur Liste" war die alte,
 * feste Ansage; sie stimmte nur, solange es eine einzige Liste gab, und ein Screenreader
 * las sie auch dann, wenn der Sprung woanders hinführte.
 *
 * Weil sie das Ziel nennt, muss sie mit ihm ALTERN: `artikel` hiess bis zum 07.09.2026
 * „Zurück zum Artikelkatalog" — der Katalog als eigene Ansicht ist mit dem Artikel-BEREICH
 * entfallen (siehe dort), also nannte der Satz eine Ansicht, die es nicht mehr gibt. Die
 * übrigen sieben Ziele wurden bei der Gelegenheit gegengelesen; ihre Wörter stehen alle
 * noch (Kundenliste, Verwaltungsbereiche, Dienste, Einstellungsgruppen).
 */
const ZURUECK_BESCHRIFTUNG: Record<Exclude<Rueckziel, object>, string> = {
  kundenliste: "Zurück zur Kundenliste",
  angebote: "Zurück zu den Angeboten",
  auftraege: "Zurück zu den Aufträgen",
  artikel: "Zurück zu den Artikeln",
  rechnungen: "Zurück zu den Rechnungen",
  verwaltung: "Zurück zu den Verwaltungsbereichen",
  schnittstellen: "Zurück zu den Diensten",
  einstellungen: "Zurück zu den Einstellungsgruppen",
};

function zurueckBeschriftung(weg: Rueckziel): string {
  // Die Objektliste eines Verwaltungsbereichs bringt ihre Beschriftung selbst mit (aus
  // VERWALTUNGS_BEREICHE) — zehn Zeilen in der Tabelle oben wären eine zweite Quelle.
  return typeof weg === "string" ? ZURUECK_BESCHRIFTUNG[weg] : weg.beschriftung;
}

/**
 * Die Vorgabe der mehrfach wählbaren Chip-Leiste: alle vier Gruppen eingeschaltet.
 *
 * Als Modulkonstante und nicht als Aufruf im Zeichnen: Der Rechnungs-Bereich führt die
 * Auswahl in seinen Abhängigkeitslisten, und ein bei jedem Durchlauf neu gebautes Array
 * wäre dort jedes Mal eine neue Referenz.
 *
 * Angebote und Aufträge brauchen kein Gegenstück: Ihre Leiste ist einfach wählbar, und
 * „alle" ist dort schlicht `null` (kein Segment in der Adresse).
 */
const ALLE_RECHNUNGSFILTER = alleFilter(RECHNUNGS_FILTER);

/** Die Adresse hinter einem Rückweg. */
function zurueckRoute(weg: Rueckziel): Route {
  // Die benannten Rückziele sind Adressen ohne Zusatzangaben; alles Übrige bringt seine
  // Adresse selbst mit (siehe den Typ oben).
  if (typeof weg !== "string") return weg.ziel;
  return weg === "kundenliste" ? { name: "kundenliste" } : { name: weg };
}

function alsFehler(ursprung: unknown): KundenFehler {
  if (ursprung instanceof ApiError) {
    // exactOptionalPropertyTypes: incidentId fehlt ganz statt explizit auf undefined zu stehen.
    return {
      message: ursprung.message,
      code: ursprung.code,
      ...(ursprung.incidentId !== undefined ? { incidentId: ursprung.incidentId } : {}),
    };
  }
  return { message: "Unerwarteter Fehler. Bitte die Seite neu laden." };
}

function zurAnmeldung(): void {
  window.location.assign("/auth/login");
}

/**
 * Verdrahtet Sitzung, API-Client und den gefalteten Rahmen (MeSchale) aus @kundeninfo/ui.
 * Kundenkopf und Tab-Leiste liegen — wie in KundenAkte.tsx beschrieben — außerhalb des
 * Inhaltsbereichs; hier auf App-Ebene gilt dieselbe Regel für die ganze Anwendung: Liste,
 * Navigation und Kontext werden je einmal gebaut, MeSchale entscheidet nur über die Faltung.
 */
export function App(): ReactElement {
  const [sitzung, setSitzung] = useState<Sitzung | null>(null);
  /*
   * Die WELT dieser Instanz — eine SITZUNGSKONSTANTE (Spec „Umgebungskennzeichnung").
   * Sie wird EINMAL mit der Sitzung gesetzt und danach nie wieder angefasst: In welchem
   * System jemand arbeitet, ändert sich innerhalb einer Sitzung nicht. Bis die Antwort da
   * ist, gilt „test" — dieselbe ausfallsichere Richtung wie serverseitig; sichtbar wird
   * das ohnehin nie, weil bis dahin die Ladeseite steht und keine Kopfleiste.
   */
  const [umgebung, setUmgebung] = useState<UmgebungsAnzeige>(
    { klasse: "test", instanz: null, konflikt: false });
  /** Der Zuschnitt aus dem Hostnamen (Spec „Belegdesign als eigenständige ME-Anwendung“) —
   *  wie die Umgebung eine Sitzungskonstante. Kosmetik, kein Sicherheitsmerkmal: Er filtert
   *  nur die Darstellung; Rechte prüft der Server (siehe zuschnitt.ts). */
  const [zuschnittSitzung, setZuschnittSitzung] = useState<Zuschnitt>("voll");
  const [sitzungFehler, setSitzungFehler] = useState<KundenFehler | null>(null);
  const [ladeSitzung, setLadeSitzung] = useState(true);

  const [search, setSearch] = useState("");
  const [items, setItems] = useState<KundeListeEintrag[]>([]);
  const [listeLaedt, setListeLaedt] = useState(false);
  const [listeFehler, setListeFehler] = useState<KundenFehler | null>(null);
  /** Hochgezählt, wenn die Liste einen Datensatz noch nicht kennen kann — nach dem
   *  Anlegen eines Kunden (Aufgabe 17) lädt der Listen-Effekt darüber neu. */
  const [listeVersion, setListeVersion] = useState(0);

  /*
   * Aufgabe 15: Auswahl und Tab leben nicht mehr in eigenem Zustand, sondern in der
   * Adresse (Browserverlauf). Damit ist jeder Bildschirm verlinkbar und die
   * Zurück-Taste bewegt sich innerhalb der Anwendung, statt sie zu verlassen.
   * Der dritte Rückgabewert wandert zum Stammdaten-Tab (Aufgabe 16): der registriert
   * bei ungespeicherten Änderungen eine Nachfrage, die jede Navigation anhält.
   */
  const [route, navigiere, registriereVerlassenWaechter] = useRoute();
  const selectedId = route.name === "kunde" ? route.id : null;
  const activeTab: AktenTabId = route.name === "kunde" ? route.tab : "uebersicht";

  /*
   * Die zuletzt geöffnete Zeile je Liste — nur für den Telefon-Einspalter (Hausregel „Scroll
   * bei Neuzeichnen", Review-Fokus 4 der ME-Etappe 2). Dort zeigt der Rahmen Liste ODER
   * Objekt; der Rückweg führt auf die Listenadresse OHNE Kennung, und die Auswahl, die aus
   * der Adresse kommt, wäre damit fort. Die Liste markiert deshalb am Telefon die zuletzt
   * geöffnete Zeile, bis eine andere geöffnet wird (den Scrollstand hält die Schale, die die
   * Liste dort nur verbirgt). Auf iPad und Desktop steht das Objekt neben der Liste — dort
   * gilt allein die Adresse.
   *
   * Ein Ref, kein Zustand: Der Wert folgt der Adresse im selben Durchlauf und löst selbst
   * nichts aus; das Fortschreiben ist idempotent.
   */
  const einspalter = useEinspalter();
  const zuletztGeoeffnet = useRef<Partial<Record<
    "kunde" | "angebote" | "auftraege" | "artikel" | "rechnungen", string>>>({});
  if ((route.name === "kunde" || route.name === "angebote" || route.name === "auftraege"
       || route.name === "artikel" || route.name === "rechnungen") && route.id !== undefined) {
    zuletztGeoeffnet.current[route.name] = route.id;
  }
  const markiert = (ziel: keyof typeof zuletztGeoeffnet.current): string | null =>
    einspalter ? (zuletztGeoeffnet.current[ziel] ?? null) : null;

  const [akte, setAkte] = useState<KundeAkteDaten | null>(null);
  const [akteFehler, setAkteFehler] = useState<KundenFehler | null>(null);
  /**
   * Hochgezählt, wenn die Akte einen Stand zeigt, den sie nicht selbst kennen kann — heute
   * nach einer Änderung an den ADRESSEN (Etappe 1): Die Hauptanschrift im Kopf ist aus der
   * Hauptadresse ABGELEITET und entsteht im Server, nicht hier.
   *
   * Ein Zähler und kein `setAkte(kunde)` aus dem Reiter heraus: Derselbe Kundensatz wäre
   * dieselbe Referenz, und React verwürfe das `setState` (Object.is) — der Kopf bliebe
   * wortlos alt stehen. Dieselbe Naht wie `listeVersion` eine Bildschirmhöhe weiter oben.
   */
  const [akteVersion, setAkteVersion] = useState(0);
  const akteNeuLaden = useCallback(() => setAkteVersion((v) => v + 1), []);
  /*
   * Der Randzustand wird beim ersten Zeichnen aus dem Browser geholt, damit die Ränder
   * sofort richtig stehen und nicht sichtbar nachrücken, wenn die Sitzung eintrifft.
   * Gemerkt wird nur, solange rememberPanels an ist (siehe die zwei Effekte unten) — liegt
   * ein Wert vor, war der Schalter zuletzt an. Steht er inzwischen auf aus (z. B. auf einem
   * anderen Gerät umgelegt, der Schalter liegt ja in der Datenbank), räumt der Effekt
   * unten auf. Ohne brauchbaren Wert gilt der Standard (siehe panelSpeicher.ts).
   */
  const [panels, setPanels] = useState<PanelState>(() => lesePanels() ?? STANDARD_PANELS);

  /**
   * Aufgabe 11: Der Verwaltungsbereich gehört ADMIN. Der Server weist alle Routen unter
   * /api/verwaltung/* für alle anderen mit 403 ab (fail closed) — dieser Wächter schützt
   * also keine Daten, er verhindert nur, dass jemand über ein Lesezeichen oder einen
   * geteilten Link auf einem Bildschirm landet, der ihm ausschliesslich Fehler zeigt.
   * Deshalb Umleitung MIT Meldung: Ein wortloser Sprung sähe aus wie ein Fehler.
   */
  const [verwaltungMeldung, setVerwaltungMeldung] = useState<string | null>(null);

  const [einstellungenOffen, setEinstellungenOffen] = useState(false);
  const [einstellungenFehler, setEinstellungenFehler] = useState<string | null>(null);
  /** Hinweis, wenn der Wechsel des Arbeitsunternehmens nicht gespeichert werden konnte. */
  const [wechselFehler, setWechselFehler] = useState<string | null>(null);
  /**
   * Kleinpaket „Service Worker: Hinweis ‚Neue Version'": registerSw.ts löst "ki:neue-
   * version" aus, sobald ein neuer Service Worker die Seite übernommen hat
   * (controllerchange, siehe registerSw.ts). Ohne diesen Hinweis sah ein offen gelassener
   * Tab nach einem Deploy nur noch weiße Seite bzw. Quelltext — der alte Worker ist weg,
   * der neue bedient Anfragen auf Dateien anders, und niemand sagte der Seite, dass sie
   * neu laden sollte.
   */
  const [neueVersionBereit, setNeueVersionBereit] = useState(false);
  useEffect(() => {
    const melde = (): void => setNeueVersionBereit(true);
    window.addEventListener("ki:neue-version", melde);
    return () => window.removeEventListener("ki:neue-version", melde);
  }, []);
  const einstellungenRef = useRef<HTMLDivElement>(null);
  // Fixrunde 1, Befund 2: Fokus setzen, Tabulator fangen, Escape schließt, Fokus beim
  // Schließen zurückgeben — als wiederverwendbarer Baustein aus @kundeninfo/ui, nicht
  // als Einzellösung hier (siehe packages/ui/src/overlay/useFocusTrap.ts).
  useFocusTrap(einstellungenOffen, einstellungenRef, () => setEinstellungenOffen(false));

  // Sitzung einmalig laden. Ohne gültige Anmeldung (401) geht es direkt zu Keycloak weiter —
  // die Oberfläche selbst kennt keinen eigenen Anmeldebildschirm, das übernimmt /auth/login.
  useEffect(() => {
    let abgebrochen = false;
    client.getSession()
      .then((antwort) => {
        if (abgebrochen) return;
        if (antwort.user === undefined) { zurAnmeldung(); return; }
        applyTheme(antwort.user.colorScheme ?? "SYSTEM", antwort.user.density ?? "COMPACT",
          schriftAus(antwort.user.fontScale ?? "AUTO", geraetErmitteln()));
        // Steht der Schalter aus, gilt der Standard — auch dann, wenn beim ersten Zeichnen
        // (vor dieser Antwort) noch ein gemerkter Wert übernommen wurde, etwa weil er auf
        // einem anderen Gerät gemerkt und der Schalter dort später umgelegt wurde. Der
        // Wechsel ist unsichtbar: Bis hierher zeigt die Anwendung ihre Ladeseite, nicht
        // den Rahmen. Verworfen wird der Wert vom Effekt weiter unten.
        if (antwort.user.rememberPanels === false) setPanels(STANDARD_PANELS);
        setUmgebung(umgebungAusSitzung(antwort));
        setZuschnittSitzung(zuschnittAusSitzung(antwort));
        setSitzung({ user: antwort.user, companies: antwort.companies ?? [] });
        setLadeSitzung(false);
      })
      .catch((ursprung) => {
        if (abgebrochen) return;
        if (ursprung instanceof ApiError && ursprung.status === 401) { zurAnmeldung(); return; }
        setSitzungFehler(alsFehler(ursprung));
        setLadeSitzung(false);
      });
    return () => { abgebrochen = true; };
  }, []);

  /*
   * Die Aktionsfarbe folgt dem HEIMATunternehmen (Spec Unternehmens-Verwaltung): Aus
   * seinem `colorHex` leitet @kundeninfo/ui die Akzent-Tokens ab und übersteuert sie am
   * Dokument — anwendungsweit über die bestehenden Tokens, keine Einzelstellen-Färbung.
   *
   * Als eigener Effekt an der FARBE, nicht als Zeile im Sitzungs-Effekt oben: So folgt sie
   * auch, wenn sich die Sitzung später ändert (ein Administrator, der sein eigenes
   * Heimatunternehmen umstellt, sähe die neue Farbe sonst erst nach einem Neuladen), und
   * die Abhängigkeit ist genau die eine Tatsache, die zählt.
   *
   * Seit ME-Etappe 1 (Aufgabe 6) folgt die Farbe dem ARBEITSUNTERNEHMEN (Wechsler in der
   * Kopfleiste; Rückfall Heimat, dann ME — siehe `arbeitsunternehmen` in session.ts). Mit
   * dem Kürzel: Die drei bekannten Unternehmen (BS/ME/PA) färbt me-design selbst über
   * `data-unternehmen`, ohne eigenen Stilblock; nur ein unbekanntes Kürzel bekommt die
   * abgeleitete Übersteuerung. Ohne jedes Unternehmen bleibt es bei den Vorgabefarben.
   */
  const arbeit = sitzung === null ? null : arbeitsunternehmen(sitzung);
  const arbeitsfarbe = arbeit?.colorHex ?? null;
  const arbeitskuerzel = arbeit?.shortCode ?? null;
  /** Dieselbe Wahl als Kennung — Vorbelegung beim Anlegen und Vorfilter der Vorlagen und
   *  Textbausteine (ME-Etappe 1, Aufgabe 7). Hier oben, weil die Bereichs-Hooks sie schon
   *  vor dem Eintreffen der Sitzung brauchen (dann `null` = Bestandsverhalten). */
  const arbeitsunternehmenId = arbeit?.id ?? null;
  useEffect(() => { setzeAktionsfarben(arbeitsfarbe, arbeitskuerzel); },
    [arbeitsfarbe, arbeitskuerzel]);

  /*
   * Reitertitel und Farbe der Browserleiste folgen der Welt (Spec: Präfix „[TEST] " bzw.
   * „[ENTW] ", `theme-color` gleich der Kopfleistenfarbe).
   *
   * Warum HIER und nicht in index.html: Das gebaute Auslieferungspaket ist umgebungsblind
   * — dasselbe dist/ läuft auf office, auf test01 und am Arbeitsplatz. Manifest, Favicon
   * und Startbildschirm-Icon kommen deshalb vom Dienst (siehe index.html); Titel und
   * `theme-color` sind Angaben IM Dokument und lassen sich nur von hier aus setzen.
   *
   * Abhängig allein von der Umgebung, nicht von der Sitzung: Die Zeile soll genau einmal
   * laufen und nicht bei jeder Änderung an Benutzer oder Unternehmen erneut.
   */
  const istAdmin = sitzung?.user.role === "ADMIN";
  /** Der WIRKSAME Zuschnitt — für Nicht-Administratoren immer „voll" (siehe zuschnitt.ts). */
  const zuschnitt = wirksamerZuschnitt(zuschnittSitzung, istAdmin);
  useEffect(() => {
    document.title = titelPraefix(umgebung) + anwendungsname(zuschnitt);
    const marke = document.querySelector('meta[name="theme-color"]');
    marke?.setAttribute("content", UMGEBUNGSFARBEN[umgebung.klasse].grund);
  }, [umgebung, zuschnitt]);

  /*
   * EINSTIEG im Zuschnitt „belegdesign": Die Kundenliste ist die Vorgabe-Route (`/`),
   * hier führt sie direkt in den Belegdesigner. Ersetzen statt Anhängen, damit die
   * Zurück-Taste nicht in eine Schleife läuft. Das Arbeitsunternehmen belegt der
   * Layout-Bereich selbst vor (views/verwaltung/layout.tsx). Kosmetik, kein
   * Sicherheitsmerkmal — eine eingetippte Kundenadresse bleibt erreichbar, soweit die
   * Rolle es hergibt.
   */
  const imZuschnittAufKundenliste = zuschnitt === "belegdesign" && route.name === "kundenliste";
  useEffect(() => {
    if (sitzung === null || !imZuschnittAufKundenliste) return;
    navigiere(zielRoute("belegdesign"), true);
  }, [sitzung, imZuschnittAufKundenliste, navigiere]);

  const willVerwaltung = route.name === "verwaltung";
  useEffect(() => {
    if (sitzung === null || !willVerwaltung || istAdmin) return;
    setVerwaltungMeldung(
      "Die Verwaltung ist Administratoren vorbehalten. Hier ist die Kundenliste.");
    // Ersetzen statt Anhängen: Sonst führte die Zurück-Taste geradewegs wieder auf die
    // Adresse, von der eben umgeleitet wurde.
    navigiere({ name: "kundenliste" }, true);
  }, [sitzung, willVerwaltung, istAdmin, navigiere]);

  // Liste neu laden, sobald die Sitzung steht oder sich die (bereits verzögert gemeldete)
  // Suche ändert.
  useEffect(() => {
    if (sitzung === null) return;
    let abgebrochen = false;
    setListeLaedt(true);
    setListeFehler(null);
    client.getCustomers({ ...(search === "" ? {} : { search }), limit: 100 })
      .then((antwort) => {
        if (abgebrochen) return;
        setItems(antwort.items);
        setListeLaedt(false);
      })
      .catch((ursprung) => {
        if (abgebrochen) return;
        setListeFehler(alsFehler(ursprung));
        setListeLaedt(false);
      });
    return () => { abgebrochen = true; };
  }, [sitzung, search, listeVersion]);

  // Akte neu laden, sobald die Auswahl wechselt. Bewusst NICHT vom Tab abhängig:
  // Ein Tabwechsel tauscht nur den Inhalt der Akte, er lädt sie nicht neu.
  // `akteVersion` wirft denselben Abruf erneut an, ohne dass die Auswahl wechselt.
  const gezeigteId = useRef<string | null>(null);
  useEffect(() => {
    if (sitzung === null || selectedId === null) {
      gezeigteId.current = null;
      setAkte(null); setAkteFehler(null); return;
    }
    let abgebrochen = false;
    // Geleert wird nur beim WECHSEL des Kunden: Beim blossen Nachladen (akteVersion) stünde
    // die Akte sonst für einen Augenblick leer da — der offene Reiter fiele mitsamt seinem
    // Zustand weg, und das ausgerechnet als Quittung für eine gelungene Änderung.
    if (gezeigteId.current !== selectedId) {
      gezeigteId.current = selectedId;
      setAkte(null);
    }
    setAkteFehler(null);
    client.getCustomer(selectedId)
      .then((k) => { if (!abgebrochen) setAkte(k); })
      .catch((ursprung) => { if (!abgebrochen) setAkteFehler(alsFehler(ursprung)); });
    return () => { abgebrochen = true; };
  }, [sitzung, selectedId, akteVersion]);

  /*
   * Aufgabe 1: `rememberPanels` wirksam machen. Der Schalter selbst steht am Benutzer in der
   * Datenbank, der Randzustand im Browser (siehe panelSpeicher.ts).
   *
   * Ist der Schalter an, wird jede Änderung am Randzustand gemerkt — auch die erste, damit
   * ein Neustart gleich nach dem Einschalten schon das Richtige wiederherstellt. Ist er aus,
   * wird nichts gemerkt UND ein vorhandener Wert verworfen: Er könnte von früher stammen
   * oder von einem anderen Gerät her noch liegen, und beim nächsten Einschalten würde er
   * sonst einen längst überholten Zustand wiederbeleben.
   */
  const sitzungSteht = sitzung !== null;
  const merken = sitzung?.user.rememberPanels ?? true;
  useEffect(() => {
    if (!sitzungSteht) return;
    if (merken) merkePanels(panels);
    else vergissPanels();
    // Bewusst NICHT von `sitzung` abhängig, sondern nur von den zwei Tatsachen, die hier
    // zählen — sonst liefe der Effekt bei jeder anderen Einstellungsänderung mit.
  }, [sitzungSteht, merken, panels]);

  /** Welche Liste in der Spalte des Rahmens steht — siehe `spalteFuer` oben. */
  const spalte = spalteFuer(route);

  /*
   * Die Angebot→Auftrag-Naht, BEREICHSINTERN (Ruling des Controllers vom 06.09.2026).
   *
   * Bis Aufgabe 3 lief sie über die Kundenakte: Der Angebots-Bereich sprang in den
   * Angebote-Reiter, dort stand derselbe Knopf noch einmal, und die Akte hielt die
   * Vorbelegung. Mit dem Auftrags-BEREICH gibt es einen Ort, an dem der neue Auftrag
   * hingehört — deshalb reist die Vorbelegung jetzt von Bereich zu Bereich.
   *
   * Sie steht in einem Zustand hier oben und NICHT in der Adresse, aus demselben Grund wie
   * `vorgangId` (siehe router.ts): Eine halb ausgefüllte Maske ist kein Bildschirm, ein
   * Lesezeichen darauf wäre sinnlos. Verbraucht wird sie sofort beim Aufnehmen
   * (`onAusAngebotAufgenommen`) — sie soll genau EINEN Sprung überleben.
   *
   * Die Kundenakte bleibt davon unberührt: Ihr eigener Weg (Angebote-Reiter →
   * Aufträge-Reiter) besteht unverändert weiter, samt seiner Vorbelegung in KundenAkte.tsx.
   */
  const [auftragAusAngebot, setAuftragAusAngebot] = useState<AuftragAusAngebot | null>(null);
  /** Stabil, weil der Auftrags-Bereich sie in einer Abhängigkeitsliste führt: Ein bei jedem
   *  Zeichnen neu gebauter Rückruf liesse seinen Aufnahme-Effekt unnötig mitlaufen. */
  const auftragAusAngebotVerbraucht = useCallback(() => setAuftragAusAngebot(null), []);

  /*
   * Dasselbe eine Stufe weiter: die Auftrag→Rechnung-Naht, seit Aufgabe 5b ebenfalls
   * BEREICHSINTERN (Entscheid Dirk 06.09.2026).
   *
   * Bis dahin führte sie über die Kundenakte — es gab keinen Rechnungs-Bereich, in den der
   * frische Entwurf gehört hätte. Jetzt gibt es ihn: Der Auftrags-Bereich leitet die
   * Rechnung ab und meldet sie hier, die Adresse wechselt auf `/rechnungen/<neueId>`, und
   * der Rechnungs-Bereich nimmt sie beim Betreten auf. Der KUNDE reist mit, weil eine
   * Rechnung nur über ihn gelesen werden kann (siehe den Kopf von BereichRechnungen.tsx).
   *
   * Die Kundenakte bleibt davon unberührt: Ihr eigener Weg (Aufträge-Reiter →
   * Rechnungen-Reiter, `oeffneRechnungId`) besteht unverändert weiter.
   */
  const [rechnungAusAuftrag, setRechnungAusAuftrag] = useState<RechnungAusAuftrag | null>(
    null);
  const rechnungAusAuftragVerbraucht = useCallback(
    () => setRechnungAusAuftrag(null), []);

  /*
   * Der Angebots-BEREICH (Etappe 1, Aufgabe 3). Er füllt ZWEI Bereiche des Rahmens —
   * Spalte und Detail — und hält den gemeinsamen Zustand selbst; deshalb liefert er seine
   * beiden Stücke als Hook (siehe views/BereichAngebote.tsx).
   *
   * Er steht hier oben, VOR den Rückgaben für Ladezustand und Sitzungsfehler: Ein Hook
   * darf nicht hinter einer Bedingung stehen. Dass er an jeder anderen Adresse nichts tut,
   * regelt `aktiv` — sonst liefe seine Suche auf jedem Bildschirm mit.
   */
  const angebotsFilter = route.name === "angebote" ? (route.filter ?? null) : null;
  const angeboteBereich = useBereichAngebote({
    // Der Riegel hängt jetzt an der ADRESSE und nicht mehr am Spalteninhalt: Seit dem
    // 07.09.2026 hat der Bereich keine Spalte mehr, über die er sich erkennen liesse.
    aktiv: route.name === "angebote",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    arbeitsunternehmenId,
    /* Der Statusfilter aus der Adresse — `null` heisst „alle" (siehe bereichsfilter.ts).
       Die Chip-Leiste zeichnet daraus ihren Zustand; ein zweiter Zustand daneben könnte
       mit der Adresse auseinanderlaufen. */
    filter: angebotsFilter,
    angebotId: route.name === "angebote" ? (route.id ?? null) : null,
    markiertId: markiert("angebote"),
    // Die bestehende Kunden-Suchauswahl, unverändert wiederverwendet: dieselbe Liste,
    // derselbe Suchtext, dieselbe Abfrage — der Bereich zeichnet sie nur an einer anderen
    // Stelle (als ersten Schritt des Anlegens).
    kunden: {
      items, loading: listeLaedt, error: listeFehler, search, onSearch: setSearch,
    },
    /* Ein Objekt aufschlagen bzw. zuklappen — der Filter BLEIBT: Die Zeile, die man
       anklickt, steht in der gefilterten Liste, und ein Sprung auf „alle" nähme sie einem
       unter den Fingern weg. */
    onOeffnen: (angebotId) => navigiere({
      name: "angebote",
      ...(angebotsFilter === null ? {} : { filter: angebotsFilter }),
      ...(angebotId === null ? {} : { id: angebotId }),
    }),
    /* Ein Filterwechsel klappt das offene Objekt zu: Es muss in der neuen Liste nicht
       stehen, und `/angebote/abgelehnt/<id>` über einer Liste ohne diese Zeile wäre eine
       Adresse, die etwas anderes behauptet, als zu sehen ist. */
    onFilter: (filter: AngebotsFilter | null) => navigiere({
      name: "angebote", ...(filter === null ? {} : { filter }),
    }),
    // „Zum Kunden" führt in die AKTE — auf ihre Übersicht, denn der Vorgang selbst steht ja
    // schon vollständig offen. `herkunft` sagt dem Rückweg auf dem iPhone, dass dahinter
    // die Angebotsliste steht und nicht die Kundenliste (siehe zurueckWeg).
    onZumKunden: (kundeId) => navigiere(
      { name: "kunde", id: kundeId, tab: "uebersicht", herkunft: "angebote" }),
    /*
     * Die Angebot→Auftrag-Naht bleibt unter den BEREICHEN (Ruling des Controllers vom
     * 06.09.2026): Die Vorbelegung wird hier abgelegt, die Adresse wechselt auf
     * `/auftraege`, und der Auftrags-Bereich nimmt sie beim Betreten auf. Nach dem Anlegen
     * steht der neue Auftrag unter `/auftraege/<neueId>` offen — dort, wo er hingehört,
     * statt in einer Akte, die man für einen einzigen Vorgang aufschlagen müsste.
     *
     * `kundeName` kommt aus der Trefferzeile des Angebots-Bereichs mit: Der Auftrags-
     * Bereich überspringt seine Kundenwahl und braucht den Namen für Überschrift und
     * Platzhalter-Vorschau, ohne den Kunden dafür zu laden.
     */
    onAuftragAusAngebot: (kundeId, kundeName, a) => {
      setAuftragAusAngebot({
        kundeId,
        kundeName,
        vorbelegung: {
          offerId: a.id,
          companyId: a.companyId,
          title: a.title,
          netAmount: a.netAmount,
          contactId: a.contactId,
          responsibleUserId: a.responsibleUserId,
          periodBasis: a.periodBasis,
        },
      });
      navigiere({ name: "auftraege" });
    },
    // Bei offener Bearbeitung fragt jede Navigation nach — dasselbe Hausmuster wie am
    // Stammdaten-Reiter, und derselbe Grund: Ein Rückweg darf Eingaben nicht stumm
    // verwerfen.
    registriereVerlassenWaechter,
  });

  /*
   * Der Auftrags-BEREICH (Etappe 1, Aufgabe 4) — dieselbe Bauart wie der Angebots-Bereich
   * darüber und aus denselben Gründen: zwei Stücke für den Rahmen, gemeinsamer Zustand im
   * Hook, `aktiv` als Riegel gegen mitlaufende Suchen an fremden Adressen.
   */
  const auftragsFilter = route.name === "auftraege" ? (route.filter ?? null) : null;
  const auftraegeBereich = useBereichAuftraege({
    aktiv: route.name === "auftraege",
    client,
    // Lösen von Abrechnungsquellen an nicht aktiven Aufträgen: nur ADMIN (Entscheid Dirk
    // 29.09.2026, Punkt 7).
    istAdmin,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    filter: auftragsFilter,
    auftragId: route.name === "auftraege" ? (route.id ?? null) : null,
    markiertId: markiert("auftraege"),
    kunden: {
      items, loading: listeLaedt, error: listeFehler, search, onSearch: setSearch,
    },
    // Das Ende der bereichsinternen Angebot→Auftrag-Naht (siehe oben).
    ausAngebot: auftragAusAngebot,
    onAusAngebotAufgenommen: auftragAusAngebotVerbraucht,
    onOeffnen: (auftragId) => navigiere({
      name: "auftraege",
      ...(auftragsFilter === null ? {} : { filter: auftragsFilter }),
      ...(auftragId === null ? {} : { id: auftragId }),
    }),
    onFilter: (filter: AuftragsFilter | null) => navigiere({
      name: "auftraege", ...(filter === null ? {} : { filter }),
    }),
    onZumKunden: (kundeId) => navigiere(
      { name: "kunde", id: kundeId, tab: "uebersicht", herkunft: "auftraege" }),
    /*
     * Die Auftrag→Rechnung-Naht — seit Aufgabe 5b BEREICHSINTERN (siehe oben): Der frische
     * Entwurf wird hier abgelegt, die Adresse wechselt auf `/rechnungen/<neueId>`, und der
     * Rechnungs-Bereich nimmt ihn beim Betreten auf. `herkunft` sagt dem Rückweg auf dem
     * iPhone, dass dahinter die Aufträge stehen und nicht die Kundenliste.
     *
     * Der Umweg über die Kundenakte entfällt damit — er war ausdrücklich der Bestandsweg
     * „bis es einen Rechnungs-Bereich gibt". Die Akte selbst bleibt unangetastet.
     */
    onRechnungAusAuftrag: (kundeId, rechnung) => {
      setRechnungAusAuftrag({ kundeId, rechnung });
      navigiere({ name: "rechnungen", id: rechnung.id, herkunft: "auftraege" });
    },
    registriereVerlassenWaechter,
  });

  /*
   * Der Artikel-BEREICH (Etappe 1, Aufgabe 5) — dieselbe Bauart wie die beiden darüber,
   * nur kundenlos: kein „Zum Kunden", keine Kundenauswahl, kein Anlegen (das bleibt der
   * Verwaltung). Er löst die bisherige Katalog-Ansicht ab; die Adresse `/artikel` bleibt.
   *
   * Auch KEIN Verlassen-Wächter: Der einzige Bearbeitungszustand ist der Änderungsdialog
   * des Bausteins, und der bleibt in dieser Aufgabe unangetastet (Begründung im Kopf von
   * views/BereichArtikel.tsx).
   */
  const artikelBereich = useBereichArtikel({
    aktiv: route.name === "artikel",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    artikelId: route.name === "artikel" ? (route.id ?? null) : null,
    markiertId: markiert("artikel"),
    istAdmin,
    onOeffnen: (artikelId) => navigiere(
      { name: "artikel", ...(artikelId === null ? {} : { id: artikelId }) }),
    // Der Verweis in die Pflege steht nur ADMINs offen — für alle anderen ist die
    // Verwaltung mit 403 verschlossen (der Server prüft das), ein Verweis dorthin führte
    // sie nur in eine sichere Sackgasse. `herkunft` bringt den Rückweg auf dem iPhone
    // wieder hierher statt zur Kundenliste.
    onPflege: () => navigiere({ name: "verwaltung", bereich: "artikel",
                                herkunft: "artikel" }),
  });

  /*
   * Der Rechnungs-BEREICH — dieselbe Bauart wie die drei darüber, mit zwei Unterschieden,
   * die beide daraus folgen, dass eine Rechnung ein BELEG ist:
   *
   *  - Es gibt KEIN Anlegen (Rechnungen entstehen am Auftrag bzw. als Korrektur).
   *  - Seine Spalte hat eine Stufe MEHR: die Status-Filterliste (Paket „Rechnungsstatus",
   *    Entscheid Dirk 06.09.2026). Der gewählte Filter ist ein Bildschirm und steht
   *    deshalb in der Adresse — genau wie der gewählte Bereich der Verwaltung. `null`
   *    heisst „Alle Rechnungen"; die Hauptnavigation führt auf `/rechnungen` und damit
   *    dorthin (siehe `zielRoute`).
   */
  const rechnungsFilter = route.name === "rechnungen"
    ? (route.filter ?? ALLE_RECHNUNGSFILTER) : ALLE_RECHNUNGSFILTER;
  const rechnungenBereich = useBereichRechnungen({
    aktiv: route.name === "rechnungen",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    /* Die eingeschalteten Status-Gruppen aus der Adresse. Anders als bei Angebot und
       Auftrag ist das eine LISTE: Die Chip-Leiste der Rechnungen ist mehrfach wählbar,
       weil ihr Server-Weg den Status wiederholbar nimmt. Kein Segment heisst „alle vier"
       (die Vorgabe), das Segment `keine` heisst „keine". */
    filter: rechnungsFilter,
    rechnungId: route.name === "rechnungen" ? (route.id ?? null) : null,
    markiertId: markiert("rechnungen"),
    // Das Ende der bereichsinternen Auftrag→Rechnung-Naht (siehe oben).
    ausAuftrag: rechnungAusAuftrag,
    onAusAuftragAufgenommen: rechnungAusAuftragVerbraucht,
    // Einen Beleg aufschlagen — die Chip-Auswahl BLEIBT (siehe die Angebote oben).
    onOeffnen: (rechnungId) => navigiere({
      name: "rechnungen",
      ...(rechnungsFilter.length >= ALLE_RECHNUNGSFILTER.length
        ? {} : { filter: rechnungsFilter }),
      ...(rechnungId === null ? {} : { id: rechnungId }),
    }),
    // Ein Filterwechsel klappt den Beleg zu, weil er in der neuen Liste nicht stehen muss.
    // „Alle vier" ergibt bewusst kein Segment: `/rechnungen` IST alle Rechnungen.
    onFilter: (filter) => navigiere({
      name: "rechnungen",
      ...(filter.length >= ALLE_RECHNUNGSFILTER.length ? {} : { filter }),
    }),
    onZumKunden: (kundeId) => navigiere(
      { name: "kunde", id: kundeId, tab: "uebersicht", herkunft: "rechnungen" }),
    registriereVerlassenWaechter,
  });

  /*
   * Der Dienste-Status (Etappe 3) — dieselbe Bauart wie die Bereiche darüber, nur ohne
   * Filter, ohne Blätterung und ohne jeden Schreibweg: Er misst beim Öffnen einmal und
   * danach auf Knopfdruck. Ein Hook ist er aus demselben Grund wie sie: Spalte und Detail
   * teilen sich EINEN Stand — die eine Messung trägt beide Seiten.
   */
  const schnittstellenBereich = useSchnittstellen({
    aktiv: spalte === "schnittstellen",
    client,
    // Nur für die Kürzel-Chips der Kundensuche im Aircall-Panel — dieselbe Liste, die die
    // Bereiche bekommen (und dieselbe stabile Referenz, siehe LEERE_UNTERNEHMEN).
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    dienstSchluessel: route.name === "schnittstellen" ? (route.dienst ?? null) : null,
    onOeffnen: (dienst) => navigiere(
      { name: "schnittstellen", ...(dienst === null ? {} : { dienst }) }),
  });

  /** Die offene Einstellungsgruppe (Etappe 3) — `null` heisst „noch keine gewählt"
   *  (Adresse `/einstellungen`). Kein Hook nötig: Die Einstellungen holen nichts vom
   *  Server, ihre Werte stehen in der Sitzung. */
  const einstellungsGruppe: EinstellungsGruppe | null =
    route.name === "einstellungen" ? (route.gruppe ?? null) : null;

  /*
   * Die OBJEKT-Bereiche der Verwaltung (Etappe 2, Aufgabe 2).
   *
   * Sie sind nach derselben Bauart gebaut wie die Bereiche der Etappe 1 darüber und stehen
   * aus demselben Grund hier: Ihr Zustand muss den BEREICHSWECHSEL überleben (Filter,
   * Suchtext und Blätterung sind die Sicht auf die Liste, keine angefangene Eingabe), und
   * ihre Verdrahtung — Adresse, Navigation, Verlassen-Wächter — gehört ohnehin hierher.
   * Die ausführliche Begründung steht an `VerwaltungBereichTeile` (gemeinsam.tsx).
   *
   * Seit dem Entscheid vom 06.09.2026 füllen sie nur noch EINE Stelle des Bildschirms: den
   * Inhalt der Verwaltung, links die Liste und rechts das gewählte Objekt. Die Spalte
   * trägt wieder allein das Bereichs-Untermenü.
   *
   * `aktiv` ist hier doppelt verriegelt: der offene Bereich UND die ADMIN-Rolle. Für alle
   * anderen ist die Verwaltung mit 403 verschlossen (der Server prüft das) — eine Abfrage
   * loszuschicken, die nur eine 403 zurückbringen kann, wäre in der kurzen Spanne bis zur
   * Umleitung (siehe der Wächter-Effekt oben) sinnlos.
   */
  const verwaltungsBereich: VerwaltungsBereich | null =
    route.name === "verwaltung" ? (route.bereich ?? null) : null;
  const verwaltungsObjektId = route.name === "verwaltung" ? (route.id ?? null) : null;
  /** Der Bereichswechsel bleibt INNERHALB der Verwaltung — die Herkunft („aus dem
   *  Artikelkatalog hierher") überlebt ihn, wie der Reiterwechsel in der Akte. */
  const verwaltungHerkunft = route.name === "verwaltung" && route.herkunft !== undefined
    ? { herkunft: route.herkunft } : {};
  /** Dieselbe Herkunft als blosser Wert — für den Rückweg am Kopf der Verwaltungs-Spalte
   *  (Aufgabe 3, siehe dort). `null` heisst „ohne fremde Herkunft hergekommen". */
  const verwaltungFremdeHerkunft: Herkunft | null =
    route.name === "verwaltung" ? (route.herkunft ?? null) : null;

  const verwaltungBenutzer = useVerwaltungBenutzer({
    aktiv: istAdmin && verwaltungsBereich === "benutzer",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    benutzerId: verwaltungsBereich === "benutzer" ? verwaltungsObjektId : null,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "benutzer", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  const verwaltungUnternehmen = useVerwaltungUnternehmen({
    aktiv: istAdmin && verwaltungsBereich === "unternehmen",
    client,
    unternehmenId: verwaltungsBereich === "unternehmen" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "unternehmen", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
    istAdmin,
  });

  /*
   * Die vier Objekt-Bereiche der Aufgabe 3 — gleiche Bauart, gleiche Verriegelung: der
   * offene Bereich UND die ADMIN-Rolle. Welche Bereiche eine Objektebene bekommen und
   * warum, steht in VERWALTUNGS_BEREICHE (views/Verwaltung.tsx).
   */
  const verwaltungKonditionen = useVerwaltungKonditionen({
    aktiv: istAdmin && verwaltungsBereich === "zahlungskonditionen",
    client,
    konditionId:
      verwaltungsBereich === "zahlungskonditionen" ? verwaltungsObjektId : null,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "zahlungskonditionen", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  /* Der Artikelstamm bekommt KEINEN Verlassen-Wächter: Sein einziger Bearbeitungszustand
     ist der Formularzustand des geteilten ArtikelDialogs, und der wohnt dort — dieselbe
     Lage und dieselbe Entscheidung wie im Artikel-Bereich der Etappe 1. */
  const verwaltungArtikel = useVerwaltungArtikel({
    aktiv: istAdmin && verwaltungsBereich === "artikel",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    artikelId: verwaltungsBereich === "artikel" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "artikel", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
  });

  const verwaltungTextbausteine = useVerwaltungTextbausteine({
    aktiv: istAdmin && verwaltungsBereich === "textbausteine",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    textbausteinId: verwaltungsBereich === "textbausteine" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "textbausteine", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  const verwaltungAngebotsvorlagen = useVerwaltungAngebotsvorlagen({
    aktiv: istAdmin && verwaltungsBereich === "angebotsvorlagen",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    vorlageId: verwaltungsBereich === "angebotsvorlagen" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "angebotsvorlagen", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  /* Der Layout-Editor (Etappe 1) — dieselbe Bauart. Der Sprung „Unternehmen öffnen"
     führt in die Unternehmensverwaltung: Akzentfarbe und Logo werden dort gepflegt (R2). */
  const verwaltungLayout = useVerwaltungLayout({
    aktiv: istAdmin && verwaltungsBereich === "layout",
    client,
    layoutId: verwaltungsBereich === "layout" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "layout", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    onUnternehmen: (companyId) => navigiere({
      name: "verwaltung", bereich: "unternehmen", id: companyId, ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  /**
   * Welcher Verwaltungsbereich seine eigene Objektliste mitbringt — als TABELLE über
   * `VerwaltungsBereich` und nicht als Kette von Vergleichen, aus demselben Grund wie bei
   * `spaltenListen` weiter unten: Kommt ein Bereich hinzu, ist dieses Objekt unvollständig
   * und der Bau bricht ab. `null` heisst „steht rechts als Ganzes" — dieselbe Aussage, die
   * `objektliste` in VERWALTUNGS_BEREICHE trifft; dass beide übereinstimmen, hält
   * packages/web/test/verwaltungsObjektliste.test.tsx fest.
   */
  const verwaltungsObjektBereiche: Record<VerwaltungsBereich, VerwaltungBereichTeile | null>
    = {
      benutzer: verwaltungBenutzer,
      unternehmen: verwaltungUnternehmen,
      zahlungskonditionen: verwaltungKonditionen,
      "ansprechpartner-rollen": null,
      dateitypen: null,
      steuersaetze: null,
      artikel: verwaltungArtikel,
      textbausteine: verwaltungTextbausteine,
      angebotsvorlagen: verwaltungAngebotsvorlagen,
      layout: verwaltungLayout,
      nummernkreise: null,
    };
  const offenerObjektBereich = verwaltungsBereich === null
    ? null : verwaltungsObjektBereiche[verwaltungsBereich];

  function abmelden(): void {
    void fetch("/auth/logout", { method: "POST", credentials: "include" })
      .finally(() => window.location.assign("/"));
  }

  /**
   * Einstellungen.tsx hat applyTheme bereits angewendet, bevor onChange hierher kommt —
   * die Auswahl wirkt also schon. Der Server-Aufruf läuft nebenläufig; scheitert er,
   * bleibt die Darstellung wie gewählt und es erscheint nur ein Hinweis (keine
   * Datenänderung, die zurückgerollt werden müsste).
   */
  function aendereEinstellungen(p: Preferences): void {
    setSitzung((s) => (s === null ? s : { ...s, user: { ...s.user, ...p } }));
    setEinstellungenFehler(null);
    client.updatePreferences(p).catch(() => {
      setEinstellungenFehler(
        "Die Darstellung konnte nicht gespeichert werden. Sie bleibt wie gewählt, bis " +
        "die Verbindung wieder klappt.");
    });
  }

  /**
   * Der Wechsel des Arbeitsunternehmens (Wechsler in der Kopfleiste). Dasselbe Muster wie
   * `aendereEinstellungen`: SOFORT in der Sitzung (Farbe und Wortmarke folgen im selben
   * Durchlauf), dann zum Server. Scheitert das Speichern, bleibt die Wahl für diese Sitzung
   * stehen und es erscheint nur ein Hinweis — beim nächsten Sitzungsabruf gilt wieder der
   * gespeicherte Wert. Die Sitzung wird danach NICHT neu geladen: Sie wüsste nichts, was
   * sie nicht schon weiss, und ein Neuladen liesse die Anwendung kurz auf „Lädt …" fallen.
   *
   * Der Server verlangt Farbschema, Dichte und Ränder-Merken in jeder Änderung (die drei
   * Pflichtfelder von PUT /api/preferences); sie gehen unverändert mit. Die Schriftstufe
   * bleibt weg — fehlt sie, behält der Server den gespeicherten Wert.
   */
  function wechsleArbeitsunternehmen(id: string): void {
    if (sitzung === null || sitzung.user.workCompanyId === id) return;
    const { colorScheme, density, rememberPanels } = sitzung.user;
    setSitzung((s) => (s === null ? s : { ...s, user: { ...s.user, workCompanyId: id } }));
    setWechselFehler(null);
    client.updatePreferences({
      colorScheme: colorScheme ?? "SYSTEM",
      density: density ?? "COMPACT",
      rememberPanels: rememberPanels ?? true,
      workCompanyId: id,
    }).catch(() => {
      setWechselFehler(
        "Das Arbeitsunternehmen konnte nicht gespeichert werden. Es bleibt für diese "
        + "Sitzung wie gewählt.");
    });
  }

  if (sitzungFehler !== null) {
    return (
      <div className="app__ladeseite" role="alert">
        {sitzungFehler.message}
      </div>
    );
  }
  if (ladeSitzung || sitzung === null) {
    return <div className="app__ladeseite">Lädt …</div>;
  }

  /** Die Wortmarke zeigt das ARBEITSunternehmen — das, unter dem gerade gearbeitet wird. */
  const firma = arbeit;

  /** Die Adresse sagt, welcher Navigationspunkt hervorgehoben ist — nicht ein zweiter
   *  Zustand daneben, der mit ihr auseinanderlaufen könnte. Alles, was kein eigenes Ziel
   *  hat (Akte, Anlegen, unbekannt), zählt zu „Kunden". */
  const navZiel: NavigationsZiel =
    // Im Zuschnitt steht der Belegdesigner für den Layout-Bereich der Verwaltung.
    zuschnitt === "belegdesign" && route.name === "verwaltung" && route.bereich === "layout"
      ? "belegdesign"
      : route.name === "verwaltung" ? "verwaltung"
      : route.name === "artikel" ? "artikel"
        : route.name === "einstellungen" ? "einstellungen"
          : route.name === "schnittstellen" ? "schnittstellen"
            : route.name === "angebote" ? "angebote"
              : route.name === "auftraege" ? "auftraege"
                : route.name === "rechnungen" ? "rechnungen"
                  : "kunden";

  const rueckweg = zurueckWeg(route);

  /**
   * Die Herkunft der GEÖFFNETEN AKTE, weitergereicht an jede Navigation, die INNERHALB
   * dieser Akte bleibt (der Reiterwechsel).
   *
   * Anders als `vorgangId`, die beim Reiterwechsel bewusst verfällt: Ein anderer Reiter ist
   * derselbe Kunde, man hat die Akte nicht verlassen — und wer aus der Angebotssuche kam,
   * will nach einem Blick auf die Stammdaten immer noch dorthin zurück. Verfallen soll sie
   * erst, wenn die Adresse selbst wechselt (Neuladen, Zurück-Taste, ein anderer Kunde) —
   * und das tut sie von selbst, weil `parseRoute` das Feld nicht kennt.
   */
  const akteHerkunft = route.name === "kunde" && route.herkunft !== undefined
    ? { herkunft: route.herkunft } : {};

  const navProps = {
    istAdmin: sitzung.user.role === "ADMIN",
    aktiv: navZiel,
    onNavigate: (ziel: NavigationsZiel): void => {
      setVerwaltungMeldung(null);
      navigiere(zielRoute(ziel));
    },
  };

  /**
   * Die Listen hinter den Spalteninhalten — als TABELLE über `SpaltenInhalt`, nicht als
   * Kette von Vergleichen (`spalte === "kunden" ? … : null`).
   *
   * Der Unterschied ist der Typprüfer: Kommt ein Spalteninhalt hinzu, ist dieses Objekt
   * unvollständig und der Bau bricht ab. Eine Vergleichskette liesse den neuen Inhalt
   * stumm auf `null` fallen — die Spalte bliebe leer, und niemand sähe je einen Fehler
   * (Review-Prüfpunkt aus Aufgabe 1; dieselbe Überlegung wie bei `spalteFuer` selbst).
   *
   * Die Einträge sind ELEMENTE, keine gezeichneten Ansichten: Was nicht in die Spalte
   * kommt, wird auch nicht gebaut.
   */
  const spaltenListen: Record<SpaltenInhalt, ReactNode> = {
    /*
     * Preis dieser Lösung, bewusst so gewählt: Die Liste wird beim Verlassen des
     * Kunden-Ziels AUSGEHÄNGT und beim Rückweg neu gebaut. Der Suchtext überlebt das von
     * selbst, weil er hier oben in App.tsx liegt (`search`); die Bildlaufposition der
     * Trefferliste geht verloren. Sie zu erhalten hiesse, die Liste dauerhaft im Baum zu
     * lassen und nur zu verstecken — ein zweiter Zustandskanal für einen Komfort, den
     * niemand verlangt hat. Festgehalten in packages/web/test/listenspalte.test.tsx.
     */
    kunden: (
      <KundenListe
        items={items}
        loading={listeLaedt}
        error={listeFehler}
        search={search}
        onSearch={setSearch}
        selectedId={selectedId ?? markiert("kunde")}
        // Der WECHSEL von einem Kunden zum nächsten behält den offenen Reiter
        // (Befund Dirk, 05.09.2026: wer Angebote durchsieht, will beim nächsten
        // Kunden wieder Angebote sehen, nicht die Übersicht). Genau dafür steht der
        // Reiter in der Adresse — der Wechsel ist dieselbe Adresse mit anderer
        // Kennung, und weil ALLE Reiter universell sind (AKTEN_TABS ist eine feste
        // Liste), gibt es dabei keinen Kunden, der den Reiter nicht hätte.
        // `activeTab` ist ausserhalb einer Akte "uebersicht" — die ERSTE Öffnung aus
        // der Liste heraus schlägt also weiterhin die Übersicht auf.
        onSelect={(id) => navigiere({ name: "kunde", id, tab: activeTab })}
        onNeu={() => navigiere({ name: "kundeNeu" })}
        companies={sitzung.companies}
      />
    ),
    /*
     * Der ARTIKEL-Bereich (Entscheid Dirk 23.09.2026): seine Spaltenliste — Filterkopf,
     * Artikelzeilen, Blätterung. Der Hook liefert Spalte und Detail als zwei Stücke, weil
     * beide sich EINEN Zustand teilen (siehe views/BereichArtikel.tsx).
     */
    artikel: artikelBereich.spalte,
    /*
     * Der ANGEBOTS-Bereich (Entscheid Dirk 23.09.2026, Aufgabe 5): seine Spaltenliste —
     * Filterkopf, Angebotszeilen, Blätterung. Derselbe Grund wie beim Artikel: Der Hook
     * liefert Spalte und Detail als zwei Stücke, weil beide sich EINEN Zustand teilen
     * (siehe views/BereichAngebote.tsx).
     */
    angebote: angeboteBereich.spalte,
    /*
     * Der AUFTRAGS-Bereich (Entscheid Dirk 23.09.2026, Aufgabe 6): seine Spaltenliste —
     * Filterkopf, Auftragszeilen, Blätterung. Derselbe Grund wie beim Angebot: Der Hook
     * liefert Spalte und Detail als zwei Stücke, weil beide sich EINEN Zustand teilen
     * (siehe views/BereichAuftraege.tsx).
     */
    auftraege: auftraegeBereich.spalte,
    /*
     * Der RECHNUNGS-Bereich (Entscheid Dirk 23.09.2026, Aufgabe 7): seine Spaltenliste —
     * Filterkopf mit der mehrfach wählbaren Chip-Leiste, Belegzeilen, Blätterung. Derselbe
     * Grund wie bei den drei Schwestern: Der Hook liefert Spalte und Detail als zwei
     * Stücke, weil beide sich EINEN Zustand teilen (siehe views/BereichRechnungen.tsx).
     */
    rechnungen: rechnungenBereich.spalte,
    /*
     * Die Schnittstellen (Etappe 3): die gemessene Dienstliste, samt dem
     * Aktualisieren-Knopf und dem Stand der Messung in ihrem Kopf — dort, wo in den
     * Bereichen der Etappe 1 das Suchfeld steht (was die Liste bestimmt, gehört zu ihr).
     */
    schnittstellen: schnittstellenBereich.spalte,
    /*
     * Die Einstellungen (Etappe 3): das Untermenü ihrer Gruppen. Dieselbe Gestalt wie das
     * Bereichs-Untermenü der Verwaltung, nur ohne zweite Ebene — eine Einstellungsgruppe
     * hat keine Objekte.
     */
    einstellungen: (
      <EinstellungenSpalte
        gruppe={einstellungsGruppe}
        onGruppe={(gruppe) => navigiere({ name: "einstellungen", gruppe })}
      />
    ),
    /*
     * Die Verwaltung (Etappe 2, Aufgabe 1): das Untermenü ihrer Bereiche.
     *
     * Nur für ADMIN — aus demselben Grund, aus dem der Navigationseintrag nur ADMIN
     * bekommt: Der Riegel liegt beim Server (403 an jeder Route), die Oberfläche soll
     * niemanden erst auf einen Bildschirm führen, der ihm ausschliesslich Fehler zeigt.
     * `null` heisst „keine Spalte" — der Rahmen zeichnet sie dann gar nicht erst (der
     * Umleitungs-Effekt oben schickt Nicht-Administratoren im selben Takt zur
     * Kundenliste).
     */
    verwaltung: sitzung.user.role === "ADMIN"
      ? (
        <VerwaltungsSpalte
          bereich={verwaltungsBereich}
          // Der Bereichswechsel bleibt INNERHALB der Verwaltung — die Herkunft
          // („aus dem Artikelkatalog hierher") überlebt ihn, wie beim Reiterwechsel
          // in der Akte. Ein WECHSEL lässt das gewählte Objekt fallen: Die Kennung
          // gehört dem Bereich, aus dem sie stammt.
          {...(zuschnitt === "belegdesign" ? { nurBereiche: BELEGDESIGN_VERWALTUNG } : {})}
          onBereich={(bereich) => navigiere({
            name: "verwaltung", bereich, ...verwaltungHerkunft })}
          /*
           * Der Weg zurück zur fremden HERKUNFT (Aufgabe 3) — heute genau einer: „Pflege in
           * der Verwaltung" aus dem Artikelkatalog. Er steht am Kopf der Spalte, weil der
           * Rahmen über der Spalte keinen Rückweg zeichnet (siehe MeSchale: dort ist man
           * normalerweise am Ursprung) — und BREIT gibt es den Kopf-Knopf des Rahmens
           * überhaupt nicht. Ohne diesen Knopf wäre der Weg aus der Artikelpflege in den
           * Katalog auf dem Schreibtisch also gar nicht gehbar.
           *
           * Auf dem iPhone trägt ihn seit dem 06.09.2026 der Rahmen: Dort zeigt
           * `/verwaltung/artikel` den INHALT (die Liste steht seither rechts, nicht mehr in
           * der Spalte), und über dem Inhalt zeichnet der Rahmen seinen Rückweg — mit
           * derselben Beschriftung, aus derselben Herkunft. Doppelt steht er nie: Der
           * Einspalter zeigt entweder die Spalte ODER den Inhalt.
           */
          {...(verwaltungFremdeHerkunft === null ? {} : { rueckweg: {
            beschriftung: ZURUECK_BESCHRIFTUNG[verwaltungFremdeHerkunft],
            aktion: () => navigiere({ name: verwaltungFremdeHerkunft }),
          } })}
        />
      )
      : null,
  };

  return (
    <>
      <MeSchale
        /*
         * Die Wortmarke geht in den eigenen Steckplatz des Rahmens und nicht in `header`:
         * Sie muss das ERSTE Kind der Kopfleiste sein — vor allem, was die Anwendung
         * beisteuert, und auf dem Telefon auch vor dem Rückweg-Knopf des Rahmens selbst
         * (Fixrunde 1, Entscheid des Controllers 08.09.2026).
         */
        wortmarke={
          <Wortmarke firma={firma} umgebung={umgebung} zuschnitt={zuschnitt}
            onHome={() => navigiere(zuschnitt === "belegdesign"
              ? zielRoute("belegdesign") : { name: "kundenliste" })} />
        }
        header={
          <Kopfzeile user={sitzung.user} firma={firma} onLogout={abmelden}
            onOpenSettings={() => setEinstellungenOffen(true)}
            umgebung={umgebung} routenschluessel={routeToPath(route)} {...navProps}
            unternehmen={sitzung.companies} zuschnitt={zuschnitt}
            onArbeitsunternehmen={wechsleArbeitsunternehmen} />
        }
        /*
         * Die untere Leiste des Telefons — seit dem 10.09.2026 der EINZIGE Ort, an dem die
         * Bereiche ausserhalb der Kopfleiste stehen. Der Steckplatz `nav` (die linke
         * Randspalte) ist mit demselben Entscheid entfallen; die Bereiche stehen auf iPad
         * und Desktop als Reihe IN der Kopfleiste, und der Inhalt bekommt ihre Breite.
         */
        tabBar={<Tableiste aktiv={navZiel} onNavigate={navProps.onNavigate}
          eintraege={bereicheFuer(zuschnitt)} />}
        list={
          /*
           * Ohne Liste zeichnet der Rahmen die Spalte gar nicht erst (siehe MeSchale.tsx) —
           * die Ansicht steht dann in voller Breite da, statt neben einem leeren Streifen.
           * WELCHE Liste es ist, entscheidet `spalteFuer` (oben), und die Tabelle
           * `spaltenListen` bildet den Inhalt typgeprüft auf sie ab.
           */
          spalte === null ? null : spaltenListen[spalte]
        }
        detail={
          route.name === "kundeNeu"
            ? <KundeAnlegen
                client={client}
                companies={sitzung.companies}
                homeCompanyId={sitzung.user.homeCompanyId ?? null}
                arbeitsunternehmenId={firma?.id ?? null}
                onFertig={(id) => {
                  // Die Liste kennt den neuen Kunden (bzw. die neue Beziehung) noch
                  // nicht — neu laden, dann in die Akte springen.
                  setListeVersion((v) => v + 1);
                  navigiere({ name: "kunde", id, tab: "uebersicht" });
                }}
                registriereVerlassenWaechter={registriereVerlassenWaechter}
              />
            : route.name === "verwaltung" && sitzung.user.role === "ADMIN"
              // Die Verwaltung zeichnet — wie die Bereiche der Etappe 1 — BEIDE Bereiche
              // des Rahmens: ihren Inhalt hier, ihr Bereichs-Untermenü oben in
              // `spaltenListen`.
              ? <Verwaltung
                  client={client}
                  bereich={route.bereich ?? null}
                  companies={sitzung.companies}
                  /*
                   * Ein Objekt-Bereich (Aufgabe 2) — seit dem Entscheid vom 06.09.2026
                   * mit BEIDEN Stücken hier: links seine Objektliste, rechts das gewählte
                   * Objekt. Bis dahin stand die Liste als zweite Ebene in der Spalte; sie
                   * war dort neben dem Bereichs-Untermenü redundant und zu schmal.
                   *
                   * `objektOffen` ist die Faltung des Einspalters: Mit gewähltem Objekt
                   * steht auf schmalem Gerät nur es da (siehe ObjektBereich).
                   */
                  {...(offenerObjektBereich === null ? {} : { objektBereich: (
                    <ObjektBereich teile={offenerObjektBereich}
                      objektOffen={verwaltungsObjektId !== null} />
                  ) })}
                />
            /*
             * Die OBJEKTBEREICHE füllen wieder ZWEI Bereiche des Rahmens — Spaltenliste oben
             * in `spaltenListen`, Detail hier (Entscheid Dirk 23.09.2026). Zwischen dem 07.
             * und dem 23.09.2026 stand jeder von ihnen GANZ hier, mit Filterkopf, Liste und
             * Detail untereinander in voller Breite. Mit Aufgabe 7 (Rechnungen) gilt das
             * wieder für alle vier.
             */
            : route.name === "artikel"
              ? artikelBereich.detail
            : route.name === "angebote"
              ? angeboteBereich.detail
            : route.name === "auftraege"
              ? auftraegeBereich.detail
            : route.name === "rechnungen"
              ? rechnungenBereich.detail
            : route.name === "schnittstellen"
              // Der Dienste-Status (Etappe 3) zeichnet — wie die Bereiche darüber — BEIDE
              // Bereiche des Rahmens: sein Detail hier, seine Dienstliste oben in
              // `spaltenListen`.
              ? schnittstellenBereich.detail
            : route.name === "einstellungen"
              // Der Menüpunkt „Einstellungen" zeigt DIESELBE Komponente wie der Dialog aus
              // dem Benutzermenü (unten) — mit denselben Werten aus der Sitzung und
              // demselben Speicherweg. Kein zweiter Stand, nur ein zweiter Zugang: der
              // eine für „ich gehe in meine Einstellungen", der andere für „schnell die
              // Ansicht umstellen, ohne den Bildschirm zu verlassen".
              //
              // Seit Etappe 3 zeigt er GENAU EINE Gruppe — die aus der Adresse, gewählt im
              // Untermenü der Spalte. Ohne Gruppe steht hier der Hinweis, wie in der
              // Verwaltung ohne Bereich; der Dialog bleibt davon unberührt und zeigt
              // weiterhin alle drei (er bekommt kein `gruppe`).
              ? <section className="app__einstellungen" aria-label="Einstellungen">
                  <h2 className="app__einstellungen-titel">Einstellungen</h2>
                  {einstellungenFehler !== null && (
                    <Hinweis art="fehler">{einstellungenFehler}</Hinweis>
                  )}
                  {einstellungsGruppe === null
                    ? <p className="app__hinweis">Bitte links eine Gruppe wählen.</p>
                    : <Einstellungen
                        colorScheme={sitzung.user.colorScheme ?? "SYSTEM"}
                        density={sitzung.user.density ?? "COMPACT"}
                        rememberPanels={sitzung.user.rememberPanels ?? true}
                        fontScale={sitzung.user.fontScale ?? "AUTO"}
                        onChange={aendereEinstellungen}
                        gruppe={einstellungsGruppe}
                      />}
                </section>
              : route.name === "unbekannt"
                ? (
                  <Hinweis art="fehler" className="app__meldung" aktionen={
                    <button type="button" onClick={() => navigiere({ name: "kundenliste" })}>
                      Zur Kundenliste
                    </button>}>
                    Diese Seite gibt es nicht.
                  </Hinweis>
                )
                : selectedId === null
                  ? verwaltungMeldung !== null
                    ? <Hinweis art="fehler" className="app__meldung">{verwaltungMeldung}</Hinweis>
                    : <p className="app__hinweis">Kein Kunde ausgewählt.</p>
                  : akteFehler !== null
                    ? <Hinweis art="fehler" className="app__meldung">{akteFehler.message}</Hinweis>
                    : akte !== null
                      ? <KundenAkte kunde={akte} activeTab={activeTab}
                          arbeitsunternehmenId={firma?.id ?? null}
                          onTab={(tab) => navigiere({
                            name: "kunde", id: selectedId, tab, ...akteHerkunft })}
                          companies={sitzung.companies}
                          // Der aus einer globalen Liste angeklickte Vorgang. Er steht in
                          // der ROUTE und nirgends sonst: Ein Tabwechsel oder die
                          // Zurück-Taste erzeugen ein Ziel ohne ihn, und damit verfällt er
                          // von selbst — ohne einen zweiten Zustand, der aufgeräumt werden
                          // müsste (vgl. die Verfallsregeln in KundenAkte.tsx).
                          {...(route.name === "kunde" && route.vorgangId !== undefined
                            ? { oeffneVorgangId: route.vorgangId } : {})}
                          // Die Monitor-Karten der Übersicht (Spec vom 08.09.2026):
                          // derselbe Klient, nur die zwei Wege, die sie braucht.
                          monitor={{ client }}
                          // Der Adressen-Reiter (Adressen Etappe 1): derselbe Klient, nur
                          // die fünf Wege, die der Baustein braucht — dazu der Ruf, der
                          // die Akte neu holt, weil die Hauptanschrift im Kopf aus den
                          // Adressen ABGELEITET ist.
                          adressen={{ client, onNeuLaden: akteNeuLaden }}
                          // Der Konzern-Reiter (Adressen Etappe 2): derselbe Klient, nur
                          // die drei Wege, die er braucht. Der frische Kundensatz aus dem
                          // Schreibweg geht an die Akte UND wirft die Kundenliste neu an:
                          // Dort steht künftig „Tochter von <Eltern>", und die Liste
                          // erführe von der Änderung sonst erst bei der nächsten Suche.
                          konzern={{
                            client,
                            onAktualisiert: (k) => {
                              setAkte(k);
                              setListeVersion((v) => v + 1);
                            },
                          }}
                          // Der Sprung in eine andere Akte — derselbe Weg wie „Zum Kunden"
                          // aus den Bereichslisten: über den Router, mit „uebersicht" als
                          // Landeplatz. Der Reiter, aus dem gesprungen wurde, reist NICHT
                          // mit: Der neue Kunde hat seinen eigenen Konzern, und dort stünde
                          // womöglich eine leere Seite.
                          onKunde={(id) => navigiere(
                            { name: "kunde", id, tab: "uebersicht" })}
                          stammdaten={{
                            client,
                            darfUebernehmen: darfSperreUebernehmen(sitzung.user),
                            // Der Aircall-Account am Kunden: nur ADMIN pflegt (R7).
                            istAdmin: sitzung.user.role === "ADMIN",
                            onAktualisiert: setAkte,
                            registriereVerlassenWaechter,
                          }}
                          ansprechpartner={{
                            client,
                            darfUebernehmen: darfSperreUebernehmen(sitzung.user),
                            registriereVerlassenWaechter,
                          }}
                          notizen={{
                            client,
                            // Eigentümer-Regel des Notiz-Reiters: bearbeiten/löschen darf
                            // der Verfasser — oder jede/r ADMIN. Der Server prüft dasselbe
                            // noch einmal in der Route (der Wächter hier blendet nur Knöpfe
                            // aus, die ohnehin mit 403 abgewiesen würden). Die Kennung ist im
                            // Sitzungstyp optional; fehlt sie (dürfte nie), blendet der ""-
                            // Rückfall die Knöpfe aus statt sie fälschlich zu zeigen — die
                            // ausfallsichere Richtung, den echten Riegel trägt ohnehin die Route.
                            angemeldeterBenutzerId: sitzung.user.id ?? "",
                            istAdmin: sitzung.user.role === "ADMIN",
                          }}
                          dateien={{ client }}
                          angebote={{ client }}
                          auftraege={{
                            client,
                            // Lösen von Abrechnungsquellen an nicht aktiven Aufträgen: nur
                            // ADMIN (Entscheid Dirk 29.09.2026, Punkt 7).
                            istAdmin: sitzung.user.role === "ADMIN",
                          }}
                          rechnungen={{ client }}
                          wichtiges={{ client }} />
                      : <p className="app__hinweis">Lädt …</p>
        }
        context={
          /*
           * Ohne Kontext zeichnet der Rahmen die Spalte gar nicht erst (siehe MeSchale.tsx)
           * — die Ansicht bekommt dann ihre Breite dazu, statt neben einem Streifen zu
           * stehen, der überall dasselbe „Kein Kunde ausgewählt." sagt. WO sie steht,
           * entscheidet `zeigtKundenKontext` (oben).
           */
          zeigtKundenKontext(route)
            ? <Kontext kunde={selectedId === null ? null : akte} />
            : null
        }
        panels={panels}
        onPanelsChange={setPanels}
        /*
         * Was der iPhone-Einspalter zeigt: die Spalte oder das Detail (siehe
         * `spalteZuerst`).
         *
         * Seit dem 23.09.2026 haben die Objektbereiche ihre Spalte zurück, also gibt es dort
         * wieder etwas zu falten. Die Ausnahmen, die das mit sich bringt, sind die
         * Anlege-Masken von Angebot und Auftrag: Sie stehen im DETAIL, haben aber keine
         * eigene Adresse (ein Lesezeichen auf ein offenes Formular wäre sinnlos, siehe
         * router.ts) — ohne einen Zusatz blieben sie auf dem iPhone unsichtbar, und die
         * Knöpfe „Neues Angebot" bzw. „Neuer Auftrag" täten scheinbar nichts. Die
         * Adressprüfung davor ist dabei nicht Beiwerk (Fixrunde 1 zu Aufgabe 3 vom
         * 05.09.2026): Ohne sie entschiede ein Zustand EINES BEREICHS über den ganzen Rahmen
         * — wer die Maske öffnete und dann auf „Kunden" wechselte, sähe dort auf dem iPhone
         * gar keine Kundenliste mehr.
         *
         * Die ANGEBOTS-Klausel kam mit Aufgabe 5 zurück, die der Aufträge folgt hier mit
         * Aufgabe 6. Der ARTIKEL-Bereich braucht keine: Er kennt keine Anlege-Maske —
         * angelegt wird allein in der Verwaltung —, und sein einziger adressloser Zustand
         * (der Änderungsdialog des Bausteins) steht ohnehin nur über einem GEÖFFNETEN
         * Artikel, wo der Einspalter schon das Detail zeigt. Der RECHNUNGS-Bereich (Aufgabe
         * 7) braucht ebenfalls keine: Eine Rechnung entsteht am Auftrag bzw. als Korrektur,
         * nicht auf Zuruf — es gibt dort keine Anlege-Maske. Damit sind alle vier
         * Objektbereiche hier bedacht.
         */
        showList={spalteZuerst(route)
          && !(route.name === "angebote" && angeboteBereich.anlageOffen)
          && !(route.name === "auftraege" && auftraegeBereich.anlageOffen)}
        // Der Rückweg des iPhone-Einspalters — WOHIN er führt, weiss nur die Adresse samt
        // ihrer Herkunft (siehe zurueckWeg oben). Ohne Rückweg bekommt der Rahmen nichts
        // und zeichnet keinen Knopf.
        {...(rueckweg === null ? {} : {
          zurueck: {
            beschriftung: zurueckBeschriftung(rueckweg),
            aktion: () => navigiere(zurueckRoute(rueckweg)),
          },
        })}
      />
      {/*
        * Die zwei schwebenden Hinweise (Entscheid Dirk 27, me-design 3311f91): `Hinweis
        * schwebend` steht unten mittig über dem Inhalt, ohne ihn zu verschieben, und mehrere
        * stapeln sich von selbst nach oben (früher der Nachbau `.app__schwebend`). Der
        * Wechsel-Fehler hat keinen eigenen Ort in einer Ansicht (der Wechsler steht in der
        * Kopfleiste) und erledigt sich nicht selbst — deshalb schliessbar.
        */}
      {wechselFehler !== null && (
        <Hinweis art="fehler" schwebend schliessbar onGeschlossen={() => setWechselFehler(null)}>
          {wechselFehler}
        </Hinweis>
      )}
      {neueVersionBereit && (
        // Kein Modal (Hausregel „Modal-Bearbeitung" gilt für Bearbeiten/Anlegen, nicht für
        // diesen Hinweis): Er schwebt wie wechselFehler über dem Inhalt, verdeckt keine
        // Eingabe und blockiert nichts — wer weiterarbeiten will, kann es einfach tun.
        // „Später" ist sein Schliessen; ein Kreuz daneben wäre ein zweiter gleicher Ausgang.
        <Hinweis art="warnung" schwebend aktionen={<>
          <button type="button" onClick={() => window.location.reload()}>Neu laden</button>
          <button type="button" onClick={() => setNeueVersionBereit(false)}>Später</button>
        </>}>
          Eine neue Version des Kundeninfo ist bereit. Bitte lade die Seite neu, sonst können
          Teile der Anwendung nicht mehr geladen werden.
        </Hinweis>
      )}
      {einstellungenOffen && (
        /* Die EINE benannte Ausnahme von der Hausregel „striktes Modalpanel" (Prüfliste in
         * striktesModal.test.tsx): Darstellung ist keine Bearbeitung — jede Wahl wirkt
         * sofort, es gibt keinen Entwurf und kein Speichern; deshalb Schliessen-Kreuz. */
        <div className="app__modal-hintergrund" role="presentation"
          onClick={() => setEinstellungenOffen(false)}>
          <div ref={einstellungenRef} tabIndex={-1} className="app__modal" role="dialog"
            aria-modal="true" aria-label="Darstellung"
            onClick={(e) => e.stopPropagation()}>
            <div className="app__modal-kopf">
              <h2>Darstellung</h2>
              <button type="button" className="app__modal-schliessen" aria-label="Schließen"
                onClick={() => setEinstellungenOffen(false)}>
                <Icon name="xmark" size={16} />
              </button>
            </div>
            {einstellungenFehler !== null && (
              <Hinweis art="fehler">{einstellungenFehler}</Hinweis>
            )}
            <Einstellungen
              colorScheme={sitzung.user.colorScheme ?? "SYSTEM"}
              density={sitzung.user.density ?? "COMPACT"}
              rememberPanels={sitzung.user.rememberPanels ?? true}
              fontScale={sitzung.user.fontScale ?? "AUTO"}
              onChange={aendereEinstellungen}
            />
          </div>
        </div>
      )}
    </>
  );
}
